import React, { useMemo, useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Box, Button, Card, FormControl, FormHelperText, FormLabel, Heading, Input, Select, Text, Textarea, VStack, Badge, List, ListItem } from "@chakra-ui/react";
import Axios from "../utils/httpClient";
import { kpiSections } from "./kpiData";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { FILE_URL, API_HOST } from "../utils/api";
import { useSelector } from "react-redux";
import Layout from "./Layout";
import { formatFullName } from "../utils/nameFormatter";
import { countriesList } from "../utils/countries";


const PRIMARY = '#133654';

// Predefined language options for dropdowns
const LANGUAGE_OPTIONS = [
  "O'zbek tili",
  "Ingliz tili",
  "Rus tili",
  "Xitoy tili",
  "Arab tili",
  "Turk tili",
  "Ispan tili",
  "Fransuz tili",
  "Nemis tili",
  "Italyan tili",
  "Portugal tili",
  "Koreys tili",
  "Yapon tili",
  "Hind tili",
  "Forsiy tili",
  "Venger tili",
  "Polyak tili",
  "Gruzin tili",
  "Qozoq tili"
];

// Searchable Country Select Component
const SearchableCountrySelect = ({ value, onChange, countries, loading, placeholder, listId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [filteredCountries, setFilteredCountries] = useState(countries);

  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = countries.filter(country =>
        country.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCountries(filtered);
    } else {
      setFilteredCountries(countries);
    }
  }, [searchTerm, countries]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    onChange(val);
    setIsOpen(true);
  };

  const handleSelectCountry = (country) => {
    setSearchTerm(country);
    onChange(country);
    setIsOpen(false);
  };

  return (
    <Box position="relative">
      <Input
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 200)}
        placeholder={loading ? "Yuklanmoqda..." : placeholder}
        isDisabled={loading}
      />
      {isOpen && filteredCountries.length > 0 && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          zIndex={1000}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          maxH="200px"
          overflowY="auto"
          mt={1}
          boxShadow="md"
        >
          <List spacing={0}>
            {filteredCountries.slice(0, 50).map(country => (
              <ListItem
                key={country}
                px={3}
                py={2}
                cursor="pointer"
                _hover={{ bg: 'gray.100' }}
                onMouseDown={() => handleSelectCountry(country)}
              >
                {country}
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
};

// Helper function to render field value (makes URLs clickable)
const renderFieldValue = (field) => {
  const isUrl = field.label && (field.label.includes('manzil') || field.label.includes('Manzil') || field.label.includes('URL'));

  if (isUrl && field.value) {
    // Add https:// if URL doesn't have a protocol
    const url = field.value.startsWith('http://') || field.value.startsWith('https://')
      ? field.value
      : `https://${field.value}`;

    return (
      <Text
        color="blue.600"
        textDecoration="underline"
        cursor="pointer"
        onClick={() => window.open(url, '_blank')}
        _hover={{ color: 'blue.800' }}
      >
        {field.value}
      </Text>
    );
  }
  return <Text>{field.value}</Text>;
};

const statusBg = (s, app) => {
  const code = app?.parameter?.code || app?.parameter?.code;

  // Multi-department approval logic
  if (code === '1.1' && (app?.tt_status !== undefined || app?.ts_status !== undefined || app?.ov_status !== undefined)) {
    const hasRejection = app.tt_status === 2 || app.ts_status === 2 || app.ov_status === 2;
    const allApproved = app.tt_status === 1 && app.ts_status === 1 && app.ov_status === 1;

    if (hasRejection) return 'rgba(231, 76, 60, 0.15)'; // Red for rejected
    if (allApproved) return 'rgba(46, 204, 113, 0.15)'; // Green for approved
    return 'rgba(241, 196, 15, 0.25)'; // Yellow for in progress
  }

  if (code === '1.3' || code === '1.4') {
    const hasRejection = app.mn_status === 2 || app.ov_status === 2;
    const allApproved = app.mn_status === 1 && app.ov_status === 1;

    if (hasRejection) return 'rgba(231, 76, 60, 0.15)';
    if (allApproved) return 'rgba(46, 204, 113, 0.15)';
    return 'rgba(241, 196, 15, 0.25)';
  }

  if (code === '3.1') {
    const hasRejection = app.tt_status === 2 || app.ts_status === 2;
    const allApproved = app.tt_status === 1 && app.ts_status === 1;

    if (hasRejection) return 'rgba(231, 76, 60, 0.15)';
    if (allApproved) return 'rgba(46, 204, 113, 0.15)';
    return 'rgba(241, 196, 15, 0.25)';
  }

  if (code === '1.6' || code === '1.7' || code === '1.8' || code === '1.9') {
    // Single department approval (O'quv bo'limi)
    if (s === 5) return 'rgba(231, 76, 60, 0.15)';
    if (s === 2 || s === 3 || s === 4) return 'rgba(46, 204, 113, 0.15)';
    return 'rgba(241, 196, 15, 0.25)';
  }

  if (String(code).startsWith('2.')) {
    // Single department approval (Ilmiy tadqiqotlar bo'limi)
    if (s === 5) return 'rgba(231, 76, 60, 0.15)';
    if (s === 2 || s === 3 || s === 4) return 'rgba(46, 204, 113, 0.15)';
    return 'rgba(241, 196, 15, 0.25)';
  }

  if (code === '3.1') {
    const hasRejection = app.tt_status === 2 || app.ts_status === 2;
    const allApproved = app.tt_status === 1 && app.ts_status === 1;

    if (hasRejection) return 'rgba(231, 76, 60, 0.15)';
    if (allApproved) return 'rgba(46, 204, 113, 0.15)';
    return 'rgba(241, 196, 15, 0.25)';
  }

  if (code === '3.2' || code === '3.3') {
    const hasRejection = app.ov_status === 2 || app.tt_status === 2 || app.ts_status === 2;
    const allApproved = app.ov_status === 1 && app.tt_status === 1 && app.ts_status === 1;

    if (hasRejection) return 'rgba(231, 76, 60, 0.15)';
    if (allApproved) return 'rgba(46, 204, 113, 0.15)';
    return 'rgba(241, 196, 15, 0.25)';
  }

  // Default status logic
  if (s === 5) return 'rgba(231, 76, 60, 0.15)';
  if (s === 2 || s === 3 || s === 4) return 'rgba(46, 204, 113, 0.15)';
  return 'rgba(241, 196, 15, 0.25)';
};

const statusText = (s, app) => {
  const code = app?.parameter?.code || app?.parameter?.code;

  // Multi-department approval logic
  if (code === '1.1' && (app?.tt_status !== undefined || app?.ts_status !== undefined || app?.ov_status !== undefined)) {
    const hasRejection = app.tt_status === 2 || app.ts_status === 2 || app.ov_status === 2;
    const allApproved = app.tt_status === 1 && app.ts_status === 1 && app.ov_status === 1;

    if (hasRejection) return 'Rad etildi';
    if (allApproved) return 'Tasdiqlandi';
    return 'Jarayonda';
  }

  if (code === '1.3' || code === '1.4') {
    const hasRejection = app.mn_status === 2 || app.ov_status === 2;
    const allApproved = app.mn_status === 1 && app.ov_status === 1;

    if (hasRejection) return 'Rad etildi';
    if (allApproved) return 'Tasdiqlandi';
    return 'Jarayonda';
  }

  if (code === '3.1') {
    const hasRejection = app.tt_status === 2 || app.ts_status === 2;
    const allApproved = app.tt_status === 1 && app.ts_status === 1;

    if (hasRejection) return 'Rad etildi';
    if (allApproved) return 'Tasdiqlandi';
    return 'Jarayonda';
  }

  if (code === '1.6' || code === '1.7' || code === '1.8' || code === '1.9') {
    // Single department approval (O'quv bo'limi)
    // Status 5 = Rejected → "Rad etildi"
    if (s === 5) return "Rad etildi";
    // Status 2,3,4 = Approved → "Tasdiqlandi"  
    if (s === 2 || s === 3 || s === 4) return "Tasdiqlandi";
    // Status 1 or other = In progress → "Jarayonda"
    return "Jarayonda";
  }

  if (String(code).startsWith('2.')) {
    // Single department approval (Ilmiy tadqiqotlar bo'limi)
    // Status 5 = Rejected → "Rad etildi"
    if (s === 5) return "Rad etildi";
    // Status 2,3,4 = Approved → "Tasdiqlandi"  
    if (s === 2 || s === 3 || s === 4) return "Tasdiqlandi";
    // Status 1 or other = In progress → "Jarayonda"
    return "Jarayonda";
  }

  if (code === '3.2' || code === '3.3') {
    const hasRejection = app.ov_status === 2 || app.tt_status === 2 || app.ts_status === 2;
    const allApproved = app.ov_status === 1 && app.tt_status === 1 && app.ts_status === 1;

    if (hasRejection) return 'Rad etildi';
    if (allApproved) return 'Tasdiqlandi';
    return 'Jarayonda';
  }

  // Default status logic
  if (s === 5) return "Rad etildi";
  if (s === 2 || s === 3 || s === 4) return "Tasdiqlandi";
  return "Jarayonda";
};

const KpiDetailTeacher = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector(s => s);
  const formRef = useRef(null);
  const [error, setError] = useState({});
  const [desc, setDesc] = useState("");
  const [files, setFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]); // Track existing files when editing
  const [myApps, setMyApps] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Check if we're coming from the welcome page
  const fromWelcome = new URLSearchParams(location.search).get('from') === 'welcome';

  // 1.1 specific fields
  const [name11, setName11] = useState("");
  const [authorsCount11, setAuthorsCount11] = useState("");
  const [approvedDate11, setApprovedDate11] = useState("");
  const [platformName11, setPlatformName11] = useState("");
  const [internetAddress11, setInternetAddress11] = useState("");
  // 1.1 requirement (from department) - total lessons count
  const [totalLessonsRequirement11, setTotalLessonsRequirement11] = useState("");

  // 1.2 specific fields  
  const [educationDirection12, setEducationDirection12] = useState("");
  const [groups12, setGroups12] = useState("");

  const [totalStudents12, setTotalStudents12] = useState("");
  const [monitoringStudents12, setMonitoringStudents12] = useState("");
  const [averageScore12, setAverageScore12] = useState("");


  // 1.3 specific fields
  const [name13, setName13] = useState("");
  const [studentInfo13, setStudentInfo13] = useState("");
  const [location13, setLocation13] = useState("");
  const [achievement13, setAchievement13] = useState("");
  const [diplomaSerial13, setDiplomaSerial13] = useState("");
  const [guidanceFile13, setGuidanceFile13] = useState(null);

  // 1.4 specific fields (similar to 1.3)
  const [name14, setName14] = useState("");
  const [studentInfo14, setStudentInfo14] = useState("");
  const [location14, setLocation14] = useState("");
  const [achievement14, setAchievement14] = useState("");
  const [diplomaSerial14, setDiplomaSerial14] = useState("");
  const [guidanceFile14, setGuidanceFile14] = useState(null);

  // 1.6 specific fields
  const [name16, setName16] = useState("");
  const [publishTime16, setPublishTime16] = useState("");
  const [authorsCount16, setAuthorsCount16] = useState("");
  const [foreignAuthors16, setForeignAuthors16] = useState("");
  const [language16, setLanguage16] = useState("");
  const [customLanguage16, setCustomLanguage16] = useState("");
  const [publishParams16, setPublishParams16] = useState("");
  const [direction16, setDirection16] = useState("");
  const [permissionFile16, setPermissionFile16] = useState(null);

  // 1.7 specific fields
  const [name17, setName17] = useState("");
  const [publishTime17, setPublishTime17] = useState("");
  const [authorsCount17, setAuthorsCount17] = useState("");
  const [foreignAuthors17, setForeignAuthors17] = useState("");
  const [language17, setLanguage17] = useState("");
  const [customLanguage17, setCustomLanguage17] = useState("");
  const [publishParams17, setPublishParams17] = useState("");
  const [direction17, setDirection17] = useState("");
  const [permissionFile17, setPermissionFile17] = useState(null);

  // 1.8 specific fields
  const [certType18, setCertType18] = useState("");
  const [level18, setLevel18] = useState("");
  const [date18, setDate18] = useState("");
  const [certFile18, setCertFile18] = useState(null);

  // 1.9 specific fields
  const [type19, setType19] = useState("");
  const [location19, setLocation19] = useState("");
  const [startDate19, setStartDate19] = useState("");
  const [endDate19, setEndDate19] = useState("");
  const [direction19, setDirection19] = useState("");
  const [purpose19, setPurpose19] = useState("");
  const [docFile19, setDocFile19] = useState(null);

  // 2.1 specific fields
  const [degreeType21, setDegreeType21] = useState("");
  const [diplomaSerial21, setDiplomaSerial21] = useState("");
  const [diplomaNumber21, setDiplomaNumber21] = useState("");
  const [diplomaDate21, setDiplomaDate21] = useState("");
  const [specialty21, setSpecialty21] = useState("");

  // 2.2 specific fields
  const [specialtyCode22, setSpecialtyCode22] = useState("");
  const [specialtyName22, setSpecialtyName22] = useState("");
  const [titleDate22, setTitleDate22] = useState("");
  const [titleSerial22, setTitleSerial22] = useState("");

  // 2.3 specific fields
  const [studentName23, setStudentName23] = useState("");
  const [studentPosition23, setStudentPosition23] = useState("");
  const [organization23, setOrganization23] = useState("");
  const [defenseDate23, setDefenseDate23] = useState("");
  const [specialtyCode23, setSpecialtyCode23] = useState("");

  // 2.4 specific fields
  const [journalName24, setJournalName24] = useState("");
  const [database24, setDatabase24] = useState("");
  const [articleName24, setArticleName24] = useState("");
  const [publishDate24, setPublishDate24] = useState("");
  const [country24, setCountry24] = useState("");
  const [authorsCount24, setAuthorsCount24] = useState("");
  const [foreignAuthors24, setForeignAuthors24] = useState("");
  const [articleLang24, setArticleLang24] = useState("");
  const [customArticleLang24, setCustomArticleLang24] = useState("");
  const [publishParams24, setPublishParams24] = useState("");
  const [profileUrl24, setProfileUrl24] = useState("");

  // 2.5 specific fields
  const [journalName25, setJournalName25] = useState("");
  const [oakRegistry25, setOakRegistry25] = useState("");
  const [articleName25, setArticleName25] = useState("");
  const [publishDate25, setPublishDate25] = useState("");
  const [country25, setCountry25] = useState("");
  const [authorsCount25, setAuthorsCount25] = useState("");
  const [foreignAuthors25, setForeignAuthors25] = useState("");
  const [articleLang25, setArticleLang25] = useState("");
  const [customArticleLang25, setCustomArticleLang25] = useState("");
  const [publishParams25, setPublishParams25] = useState("");
  const [articleUrl25, setArticleUrl25] = useState("");

  // 2.6 specific fields
  const [profileUrl26, setProfileUrl26] = useState("");
  const [database26, setDatabase26] = useState("");
  const [articleCount26, setArticleCount26] = useState("");
  const [hIndex26, setHIndex26] = useState("");

  // 2.7 specific fields
  const [journalName27, setJournalName27] = useState("");
  const [oakRegistry27, setOakRegistry27] = useState("");
  const [articleName27, setArticleName27] = useState("");
  const [publishDate27, setPublishDate27] = useState("");
  const [authorsCount27, setAuthorsCount27] = useState("");
  const [foreignAuthors27, setForeignAuthors27] = useState("");
  const [articleLang27, setArticleLang27] = useState("");
  const [customArticleLang27, setCustomArticleLang27] = useState("");
  const [publishParams27, setPublishParams27] = useState("");
  const [articleUrl27, setArticleUrl27] = useState("");

  // 2.8 specific fields
  const [patentName28, setPatentName28] = useState("");
  const [patentType28, setPatentType28] = useState("");
  const [patentDate28, setPatentDate28] = useState("");
  const [registrationNumber28, setRegistrationNumber28] = useState("");
  const [ownersCount28, setOwnersCount28] = useState("");

  // 2.9 specific fields
  const [projectName29, setProjectName29] = useState("");
  const [contractDate29, setContractDate29] = useState("");
  const [totalAmount29, setTotalAmount29] = useState("");
  const [participantsCount29, setParticipantsCount29] = useState("");
  const [receivedAmount29, setReceivedAmount29] = useState("");

  // 2.10 specific fields
  const [grantName210, setGrantName210] = useState("");
  const [grantType210, setGrantType210] = useState("");
  const [projectNumber210, setProjectNumber210] = useState("");
  const [contractNumber210, setContractNumber210] = useState("");
  const [projectDuration210, setProjectDuration210] = useState("");
  const [totalSum210, setTotalSum210] = useState("");
  const [participantsCount210, setParticipantsCount210] = useState("");

  // 2.11 specific fields
  const [councilName211, setCouncilName211] = useState("");
  const [organizationName211, setOrganizationName211] = useState("");
  const [specialty211, setSpecialty211] = useState("");
  const [position211, setPosition211] = useState("");
  const [membershipDate211, setMembershipDate211] = useState("");

  // 3.1 specific fields
  const [foreignCountry31, setForeignCountry31] = useState("");
  const [foreignUniversity31, setForeignUniversity31] = useState("");
  const [degreeDate31, setDegreeDate31] = useState("");
  const [degreeLevel31, setDegreeLevel31] = useState("");
  const [diplomaSerial31, setDiplomaSerial31] = useState("");
  const [specialtyName31, setSpecialtyName31] = useState("");

  // 3.2 specific fields
  const [foreignCountry32, setForeignCountry32] = useState("");
  const [foreignUniversity32, setForeignUniversity32] = useState("");
  const [specialtyName32, setSpecialtyName32] = useState("");
  const [lectureType32, setLectureType32] = useState("");
  const [duration32, setDuration32] = useState("");

  // 3.3 specific fields
  const [trainingType33, setTrainingType33] = useState("");
  const [trainingLocation33, setTrainingLocation33] = useState("");
  const [trainingDate33, setTrainingDate33] = useState("");
  const [trainingDirection33, setTrainingDirection33] = useState("");
  const [trainingPurpose33, setTrainingPurpose33] = useState("");

  // 3.4 specific fields
  const [cooperationName34, setCooperationName34] = useState("");
  const [cooperationLocation34, setCooperationLocation34] = useState("");
  const [cooperationDate34, setCooperationDate34] = useState("");
  const [cooperationDirection34, setCooperationDirection34] = useState("");
  const [cooperationPurpose34, setCooperationPurpose34] = useState("");

  // 4.1 specific fields
  const [eventName41, setEventName41] = useState("");
  const [eventDate41, setEventDate41] = useState("");
  const [eventLocation41, setEventLocation41] = useState("");

  // 4.2 specific fields
  const [clubName42, setClubName42] = useState("");
  const [clubRegulations42, setClubRegulations42] = useState("");

  // 4.3 specific fields
  const [activityName43, setActivityName43] = useState("");
  const [activityDate43, setActivityDate43] = useState("");
  const [activityLocation43, setActivityLocation43] = useState("");

  // 4.4 specific fields
  const [projectName44, setProjectName44] = useState("");
  const [projectDate44, setProjectDate44] = useState("");
  const [projectLocation44, setProjectLocation44] = useState("");

  // 4.5 specific fields
  const [competitionName45, setCompetitionName45] = useState("");
  const [competitionType45, setCompetitionType45] = useState("");
  const [studentFish45, setStudentFish45] = useState("");
  const [competitionLocation45, setCompetitionLocation45] = useState("");
  const [competitionDate45, setCompetitionDate45] = useState("");
  const [achievement45, setAchievement45] = useState("");
  const [diplomaSerial45, setDiplomaSerial45] = useState("");

  // 4.6 specific fields
  const [mediaSubject46, setMediaSubject46] = useState("");
  const [mediaDate46, setMediaDate46] = useState("");
  const [mediaName46, setMediaName46] = useState("");

  // 4.7 specific fields
  const [name47, setName47] = useState("");
  const [time47, setTime47] = useState("");
  const [report47, setReport47] = useState("");

  // State for requirement applications
  const [requirementApps, setRequirementApps] = useState([]);
  const [paramFromApi, setParamFromApi] = useState(null);

  // Countries list for dropdown (statically loaded)
  const [countries, setCountries] = useState(countriesList);
  const [loadingCountries, setLoadingCountries] = useState(false);

  const param = useMemo(() => {
    const all = kpiSections.flatMap(s => s.items);
    return all.find(p => String(p.no) === String(id));
  }, [id]);

  // Fetch parameter details from API to get required_percentage
  useEffect(() => {
    if (id) {
      console.log('🔄 Fetching parameter details for code:', id);
      // Fetch all parameters and find by code
      Axios().get('api/v1/application/kpi-parameters/')
        .then(res => {
          const categories = res.data?.categories || [];
          const allParams = categories.flatMap(cat => cat.parameters || []);
          const foundParam = allParams.find(p => String(p.code) === String(id));
          console.log('✅ Found parameter from API:', foundParam);
          setParamFromApi(foundParam);
        })
        .catch(err => {
          console.error('❌ Error loading parameter details:', err);
        });
    }
  }, [id]);

  // Helper functions
  const is11 = String(param?.no) === '1.1';
  const is12 = String(param?.no) === '1.2';
  const is13 = String(param?.no) === '1.3';
  const is14 = String(param?.no) === '1.4';
  const is16 = String(param?.no) === '1.6';
  const is17 = String(param?.no) === '1.7';
  const is18 = String(param?.no) === '1.8';
  const is19 = String(param?.no) === '1.9';
  const is21 = String(param?.no) === '2.1';
  const is22 = String(param?.no) === '2.2';
  const is23 = String(param?.no) === '2.3';
  const is24 = String(param?.no) === '2.4';
  const is25 = String(param?.no) === '2.5';
  const is26 = String(param?.no) === '2.6';
  const is27 = String(param?.no) === '2.7';
  const is28 = String(param?.no) === '2.8';
  const is29 = String(param?.no) === '2.9';
  const is210 = String(param?.no) === '2.10';
  const is211 = String(param?.no) === '2.11';
  const is31 = String(param?.no) === '3.1';
  const is32 = String(param?.no) === '3.2';
  const is33 = String(param?.no) === '3.3';
  const is34 = String(param?.no) === '3.4';
  const is41 = String(param?.no) === '4.1';
  const is42 = String(param?.no) === '4.2';
  const is43 = String(param?.no) === '4.3';
  const is44 = String(param?.no) === '4.4';
  const is45 = String(param?.no) === '4.5';
  const is46 = String(param?.no) === '4.6';
  const is47 = String(param?.no) === '4.7';

  // Set countries list
  useEffect(() => {
    setCountries(countriesList);
  }, []);

  const loadMyApps = () => {
    if (!param?.no) return;
    setLoadingList(true);

    // Load regular applications
    Axios().get(`api/v1/application/my-list-by-param/`, {
      params: { parameter_code: String(param.no) }
    })
      .then(res => {
        const applications = res?.data?.applications || res?.data || [];
        // Filter out requirement applications (status=6) to avoid duplicates
        const regularApps = applications.filter(app => app.status !== 6);
        setMyApps(regularApps);
      })
      .catch(e => {
        console.error('Error loading applications:', e);
        setMyApps([]);
      })
      .finally(() => setLoadingList(false));
  };

  // Load requirement applications (status=6) created by departments for this teacher
  const loadRequirementApps = () => {
    if (!param?.no) return;

    Axios().get(`api/v1/application/my-requirements-by-param/`, {
      params: { parameter_code: String(param.no) }
    })
      .then(res => {
        const applications = res?.data?.applications || res?.data || [];
        console.log('Loaded requirement applications:', applications);
        setRequirementApps(applications);

        // Extract totalLessons for KPI 1.1 (first available requirement)
        if (String(param?.no) === '1.1') {
          try {
            let found = '';
            for (const app of applications) {
              const workText = app?.work || '';
              if (workText) {
                // JSON
                if (!found && workText.trim().startsWith('{')) {
                  try { const parsed = JSON.parse(workText); if (parsed?.totalLessons) found = String(parsed.totalLessons); } catch { }
                }
                // Regex fallback
                if (!found) {
                  const m = workText.match(/totalLessons\"?:\s*\"?(\d+)\"?/);
                  if (m && m[1]) found = m[1];
                }
              }
              if (!found && app?.requirement) {
                const m2 = String(app.requirement).match(/darslar\s+soni[:\s-]*?(\d+)/i);
                if (m2 && m2[1]) found = m2[1];
              }
              if (found) break;
            }
            if (found) setTotalLessonsRequirement11(found);
          } catch { }
        }
      })
      .catch(e => {
        console.error('Error loading requirement applications:', e);
        setRequirementApps([]);
      });
  };

  useEffect(() => {
    loadMyApps();
    loadRequirementApps(); // Load requirement applications as well
  }, [param?.no]);

  // Reload data when page becomes visible (e.g., when navigating back from kafedra distribution)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && param?.no) {
        console.log('Page became visible, reloading data...');
        loadMyApps();
        loadRequirementApps();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [param?.no]);

  // Reload data when navigating back to this page
  useEffect(() => {
    if (param?.no) {
      console.log('Location or param changed, reloading data...');
      loadMyApps();
      loadRequirementApps();
    }
  }, [location.key, param?.no]);

  const submit = (e) => {
    e.preventDefault();
    let t = true, err = {};
    setError({});

    // Validation based on parameter type
    if (is11) {
      if (!name11) { t = false; err = { ...err, name11: true }; }
      if (!authorsCount11) { t = false; err = { ...err, authorsCount11: true }; }
      if (!approvedDate11) { t = false; err = { ...err, approvedDate11: true }; }
      if (!platformName11) { t = false; err = { ...err, platformName11: true }; }
      if (!internetAddress11) {
        t = false;
        err = { ...err, internetAddress11: true };
      }

      // Validate URL format - must start with http:// or https://
      if (internetAddress11 && t) {
        if (!internetAddress11.startsWith('http://') && !internetAddress11.startsWith('https://')) {
          t = false;
          err = { ...err, internetAddress11: true, internetAddress11Message: "Havola yaroqsiz" };
        }
      }

      // Check if URL already exists
      if (internetAddress11 && t) {
        // Check against existing applications (exclude current app when editing)
        const urlExists = myApps.some(app => {
          // Skip the current application being edited
          if (editId && app.id === editId) {
            return false;
          }

          try {
            const appData = JSON.parse(app.work || '{}');
            const appUrl = appData.internetAddress || appData.url || appData.internetManzili;
            return appUrl && appUrl.toLowerCase().trim() === internetAddress11.toLowerCase().trim();
          } catch {
            return false;
          }
        });

        if (urlExists) {
          t = false;
          err = { ...err, internetAddress11: true, internetAddress11Message: "Bu havola oldin kiritilgan" };
        }
      }
    } else if (is12) {
      if (!educationDirection12) { t = false; err = { ...err, educationDirection12: true }; }
      if (!totalStudents12) { t = false; err = { ...err, totalStudents12: true }; }
      if (!monitoringStudents12) { t = false; err = { ...err, monitoringStudents12: true }; }
      if (!averageScore12) { t = false; err = { ...err, averageScore12: true }; }
    } else if (is13) {
      if (!name13) { t = false; err = { ...err, name13: true }; }
      if (!studentInfo13) { t = false; err = { ...err, studentInfo13: true }; }
      if (!achievement13) { t = false; err = { ...err, achievement13: true }; }
    } else if (is14) {
      if (!name14) { t = false; err = { ...err, name14: true }; }
      if (!studentInfo14) { t = false; err = { ...err, studentInfo14: true }; }
      if (!achievement14) { t = false; err = { ...err, achievement14: true }; }
    } else if (is16) {
      if (!name16) { t = false; err = { ...err, name16: true }; }
      if (!publishTime16) { t = false; err = { ...err, publishTime16: true }; }
      if (!authorsCount16) { t = false; err = { ...err, authorsCount16: true }; }
    } else if (is17) {
      if (!name17) { t = false; err = { ...err, name17: true }; }
      if (!publishTime17) { t = false; err = { ...err, publishTime17: true }; }
      if (!authorsCount17) { t = false; err = { ...err, authorsCount17: true }; }
    } else if (is18) {
      if (!certType18) { t = false; err = { ...err, certType18: true }; }
      if (!level18) { t = false; err = { ...err, level18: true }; }
      if (!date18) { t = false; err = { ...err, date18: true }; }
    } else if (is19) {
      if (!type19) { t = false; err = { ...err, type19: true }; }
      if (!location19) { t = false; err = { ...err, location19: true }; }
      if (!startDate19) { t = false; err = { ...err, startDate19: true }; }
      if (!endDate19) { t = false; err = { ...err, endDate19: true }; }
    } else if (is21) {
      if (!degreeType21) { t = false; err = { ...err, degreeType21: true }; }
      if (!diplomaSerial21) { t = false; err = { ...err, diplomaSerial21: true }; }
    } else if (is22) {
      if (!specialtyCode22) { t = false; err = { ...err, specialtyCode22: true }; }
      if (!specialtyName22) { t = false; err = { ...err, specialtyName22: true }; }
    } else if (is23) {
      // No required fields for 2.3
    } else if (is24) {
      if (!journalName24) { t = false; err = { ...err, journalName24: true }; }
      if (!articleName24) { t = false; err = { ...err, articleName24: true }; }
      // Validate profile URL format if provided - must start with http:// or https://
      if (profileUrl24 && profileUrl24.trim() !== '') {
        if (!profileUrl24.startsWith('http://') && !profileUrl24.startsWith('https://')) {
          t = false;
          err = { ...err, profileUrl24: true, profileUrl24Message: "Havola http:// yoki https:// bilan boshlanishi kerak" };
        }
      }
    } else if (is25) {
      // Validate article URL format if provided - must start with http:// or https://
      if (articleUrl25 && articleUrl25.trim() !== '') {
        if (!articleUrl25.startsWith('http://') && !articleUrl25.startsWith('https://')) {
          t = false;
          err = { ...err, articleUrl25: true, articleUrl25Message: "Havola http:// yoki https:// bilan boshlanishi kerak" };
        }
      }
    } else if (is26) {
      // Validate profile URL format if provided - must start with http:// or https://
      if (profileUrl26 && profileUrl26.trim() !== '') {
        if (!profileUrl26.startsWith('http://') && !profileUrl26.startsWith('https://')) {
          t = false;
          err = { ...err, profileUrl26: true, profileUrl26Message: "Havola http:// yoki https:// bilan boshlanishi kerak" };
        }
      }
    } else if (is27) {
      // Validate article URL format if provided - must start with http:// or https://
      if (articleUrl27 && articleUrl27.trim() !== '') {
        if (!articleUrl27.startsWith('http://') && !articleUrl27.startsWith('https://')) {
          t = false;
          err = { ...err, articleUrl27: true, articleUrl27Message: "Havola http:// yoki https:// bilan boshlanishi kerak" };
        }
      }
    } else if (is28) {
      // No required fields for 2.8
    } else if (is29) {
      // No required fields for 2.9
    } else if (is210) {
      // No required fields for 2.10
    } else if (is211) {
      // No required fields for 2.11
    } else if (is31) {
      // No required fields for 3.1
    } else if (is32) {
      // No required fields for 3.2
    } else if (is33) {
      // No required fields for 3.3
    } else if (is34) {
      // No required fields for 3.4
    } else if (is41) {
      // No required fields for 4.1
    } else if (is42) {
      // No required fields for 4.2
    } else if (is43) {
      // No required fields for 4.3
    } else if (is44) {
      // No required fields for 4.4
    } else if (is45) {
      // No required fields for 4.5
    } else if (is46) {
      // No required fields for 4.6
    } else if (is47) {
      if (!name47) { t = false; err = { ...err, name47: true }; }
      if (!time47) { t = false; err = { ...err, time47: true }; }
      if (!report47) { t = false; err = { ...err, report47: true }; }
    } else {
      if (!desc) { t = false; err = { ...err, desc: true }; }
    }

    // Validate that at least one file is uploaded (required for all parameters)
    // When editing, allow keeping existing files
    // For 1.3 and 1.4: either main files or guidance file counts as "file uploaded"
    const hasAnyFile = files.length > 0 ||
      (is13 && guidanceFile13) ||
      (is14 && guidanceFile14);
    if (!hasAnyFile && (!editId || existingFiles.length === 0)) {
      t = false;
      err = { ...err, files: true, filesMessage: "Kamida bitta PDF fayl yuklash majburiy" };
    }

    if (t) {
      const form = new FormData();

      // Structure data based on parameter type
      if (is11) {
        const data = {
          name: name11,
          authorsCount: authorsCount11,
          approvedDate: approvedDate11,
          platformName: platformName11,
          internetAddress: internetAddress11,
          totalLessonsRequirement: totalLessonsRequirement11 || undefined
        };
        form.append('work', JSON.stringify(data));

      } else if (is12) {
        const data = {
          educationDirection: educationDirection12,
          groups: groups12,
          totalStudents: totalStudents12,
          monitoringStudents: monitoringStudents12,
          averageScore: averageScore12
        };
        form.append('work', JSON.stringify(data));
      } else if (is13) {
        const data = {
          name: name13,
          studentInfo: studentInfo13,
          location: location13,
          achievement: achievement13,
          diplomaSerial: diplomaSerial13
        };
        form.append('work', JSON.stringify(data));
        // Add guidance file separately if provided
        if (guidanceFile13) {
          form.append('guidance_file', guidanceFile13, 'Rahbarlik_hujjati_' + (guidanceFile13.name || 'fayl.pdf'));
        }
      } else if (is14) {
        const data = {
          name: name14,
          studentInfo: studentInfo14,
          location: location14,
          achievement: achievement14,
          diplomaSerial: diplomaSerial14
        };
        form.append('work', JSON.stringify(data));
        // Add guidance file separately if provided
        if (guidanceFile14) {
          form.append('guidance_file', guidanceFile14, 'Rahbarlik_hujjati_' + (guidanceFile14.name || 'fayl.pdf'));
        }
      } else if (is16) {
        const data = {
          name: name16,
          publishTime: publishTime16,
          authorsCount: authorsCount16,
          foreignAuthors: foreignAuthors16 || 0,
          language: language16 === 'Boshqa' ? customLanguage16 : language16,
          publishParams: publishParams16,
          direction: direction16
        };
        form.append('work', JSON.stringify(data));
        // Add permission file separately if provided
        if (permissionFile16) {
          form.append('permission_file', permissionFile16, 'Ruxsatnoma_fayli_' + (permissionFile16.name || 'fayl.pdf'));
        }
      } else if (is17) {
        const data = {
          name: name17,
          publishTime: publishTime17,
          authorsCount: authorsCount17,
          foreignAuthors: foreignAuthors17 || 0,
          language: language17 === 'Boshqa' ? customLanguage17 : language17,
          publishParams: publishParams17,
          direction: direction17
        };
        form.append('work', JSON.stringify(data));
        // Add permission file separately if provided
        if (permissionFile17) {
          form.append('permission_file', permissionFile17, 'Ruxsatnoma_fayli_' + (permissionFile17.name || 'fayl.pdf'));
        }
      } else if (is18) {
        const data = {
          certType: certType18,
          level: level18,
          date: date18
        };
        form.append('work', JSON.stringify(data));
        // Add certificate file separately if provided
        if (certFile18) {
          form.append('cert_file', certFile18, 'Sertifikat_fayli_' + (certFile18.name || 'fayl.pdf'));
        }
      } else if (is19) {
        const data = {
          type: type19,
          location: location19,
          startDate: startDate19,
          endDate: endDate19,
          direction: direction19,
          purpose: purpose19
        };
        form.append('work', JSON.stringify(data));
        // Add document file separately if provided
        if (docFile19) {
          form.append('doc_file', docFile19, 'Hujjat_fayli_' + (docFile19.name || 'fayl.pdf'));
        }
      } else if (is21) {
        const data = {
          degreeType: degreeType21,
          diplomaSerial: diplomaSerial21,
          diplomaNumber: diplomaNumber21,
          diplomaDate: diplomaDate21,
          specialty: specialty21
        };
        form.append('work', JSON.stringify(data));
      } else if (is22) {
        const data = {
          specialtyCode: specialtyCode22,
          specialtyName: specialtyName22,
          titleDate: titleDate22,
          titleSerial: titleSerial22
        };
        form.append('work', JSON.stringify(data));
      } else if (is24) {
        const data = {
          journalName: journalName24,
          database: database24,
          articleName: articleName24,
          publishDate: publishDate24,
          country: country24,
          authorsCount: authorsCount24,
          foreignAuthors: foreignAuthors24 || 0,
          articleLang: articleLang24 === 'Boshqa' ? customArticleLang24 : articleLang24,
          publishParams: publishParams24,
          profileUrl: profileUrl24
        };
        form.append('work', JSON.stringify(data));
      } else if (is23) {
        const data = {
          studentName: studentName23,
          studentPosition: studentPosition23,
          organization: organization23,
          defenseDate: defenseDate23,
          specialtyCode: specialtyCode23
        };
        form.append('work', JSON.stringify(data));
      } else if (is25) {
        const data = {
          journalName: journalName25,
          oakRegistry: oakRegistry25,
          articleName: articleName25,
          publishDate: publishDate25,
          country: country25,
          authorsCount: authorsCount25,
          foreignAuthors: foreignAuthors25 || 0,
          articleLang: articleLang25 === 'Boshqa' ? customArticleLang25 : articleLang25,
          publishParams: publishParams25,
          articleUrl: articleUrl25
        };
        form.append('work', JSON.stringify(data));
      } else if (is26) {
        const data = {
          database: database26,
          profileUrl: profileUrl26,
          articleCount: articleCount26,
          hIndex: hIndex26
        };
        form.append('work', JSON.stringify(data));
      } else if (is27) {
        const data = {
          journalName: journalName27,
          oakRegistry: oakRegistry27,
          articleName: articleName27,
          publishDate: publishDate27,
          authorsCount: authorsCount27,
          foreignAuthors: foreignAuthors27 || 0,
          articleLang: articleLang27 === 'Boshqa' ? customArticleLang27 : articleLang27,
          publishParams: publishParams27,
          articleUrl: articleUrl27
        };
        form.append('work', JSON.stringify(data));
      } else if (is28) {
        const data = {
          patentName: patentName28,
          patentType: patentType28,
          patentDate: patentDate28,
          registrationNumber: registrationNumber28,
          ownersCount: ownersCount28
        };
        form.append('work', JSON.stringify(data));
      } else if (is29) {
        const data = {
          projectName: projectName29,
          contractDate: contractDate29,
          totalAmount: totalAmount29,
          participantsCount: participantsCount29,
          receivedAmount: receivedAmount29
        };
        form.append('work', JSON.stringify(data));
      } else if (is210) {
        const data = {
          grantName: grantName210,
          grantType: grantType210,
          projectNumber: projectNumber210,
          contractNumber: contractNumber210,
          projectDuration: projectDuration210,
          totalSum: totalSum210,
          participantsCount: participantsCount210
        };
        form.append('work', JSON.stringify(data));
      } else if (is211) {
        const data = {
          councilName: councilName211,
          organizationName: organizationName211,
          specialty: specialty211,
          position: position211,
          membershipDate: membershipDate211
        };
        form.append('work', JSON.stringify(data));
      } else if (is31) {
        const data = {
          foreignCountry: foreignCountry31,
          foreignUniversity: foreignUniversity31,
          degreeDate: degreeDate31,
          degreeLevel: degreeLevel31,
          diplomaSerial: diplomaSerial31,
          specialtyName: specialtyName31
        };
        form.append('work', JSON.stringify(data));
      } else if (is32) {
        const data = {
          foreignCountry: foreignCountry32,
          foreignUniversity: foreignUniversity32,
          specialtyName: specialtyName32,
          lectureType: lectureType32,
          duration: duration32
        };
        form.append('work', JSON.stringify(data));
      } else if (is33) {
        const data = {
          trainingType: trainingType33,
          trainingLocation: trainingLocation33,
          trainingDate: trainingDate33,
          trainingDirection: trainingDirection33,
          trainingPurpose: trainingPurpose33
        };
        form.append('work', JSON.stringify(data));
      } else if (is34) {
        const data = {
          cooperationName: cooperationName34,
          cooperationLocation: cooperationLocation34,
          cooperationDate: cooperationDate34,
          cooperationDirection: cooperationDirection34,
          cooperationPurpose: cooperationPurpose34
        };
        form.append('work', JSON.stringify(data));
      } else if (is41) {
        const data = {
          eventName: eventName41,
          eventDate: eventDate41,
          eventLocation: eventLocation41
        };
        form.append('work', JSON.stringify(data));
      } else if (is42) {
        const data = {
          clubName: clubName42,
          clubRegulations: clubRegulations42
        };
        form.append('work', JSON.stringify(data));
      } else if (is43) {
        const data = {
          activityName: activityName43,
          activityDate: activityDate43,
          activityLocation: activityLocation43
        };
        form.append('work', JSON.stringify(data));
      } else if (is44) {
        const data = {
          projectName: projectName44,
          projectDate: projectDate44,
          projectLocation: projectLocation44
        };
        form.append('work', JSON.stringify(data));
      } else if (is45) {
        const data = {
          competitionName: competitionName45,
          competitionType: competitionType45,
          studentFish: studentFish45,
          competitionLocation: competitionLocation45,
          competitionDate: competitionDate45,
          achievement: achievement45,
          diplomaSerial: diplomaSerial45
        };
        form.append('work', JSON.stringify(data));
      } else if (is46) {
        const data = {
          mediaSubject: mediaSubject46,
          mediaDate: mediaDate46,
          mediaName: mediaName46
        };
        form.append('work', JSON.stringify(data));
      } else if (is47) {
        const data = {
          name: name47,
          time: time47,
          report: report47
        };
        form.append('work', JSON.stringify(data));
      } else {
        form.append('work', desc);
      }

      files.forEach(f => form.append('files', f));
      form.append('parameter_code', param.no);

      const url = editId ? `api/v1/application/update/${editId}/` : 'api/v1/application/create/';
      const method = editId ? 'put' : 'post';

      Axios({ multipart: true })[method](url, form)
        .then(res => {
          loadMyApps();
          cancelEdit();
          setShowForm(false);
          // Show success message
          if (editId) {
            alert('Ariza muvaffaqiyatli tahrirlandi va qayta ko\'rib chiqish uchun yuborildi. Ariza "Baholanmaganlar" bo\'limida ko\'rinadi.');
          }
        })
        .catch(e => {
          console.error('Submit error:', e);
          const detailMsg = e.response?.data?.detail || e.response?.data?.message || JSON.stringify(e.response?.data) || e.message;
          console.error('Submit error detail:', detailMsg);
          setError({ common_e: `Xatolik yuz berdi: ${detailMsg}` });
        });
    } else {
      setError(err);
    }
  };

  const cancelEdit = () => {
    setEditId(null);
    setError({});
    setDesc("");
    setFiles([]);
    setExistingFiles([]);
    // Reset all specific fields
    setName11(""); setAuthorsCount11(""); setApprovedDate11(""); setPlatformName11(""); setInternetAddress11("");
    setEducationDirection12(""); setGroups12(""); setTotalStudents12(""); setMonitoringStudents12(""); setAverageScore12("");
    setName13(""); setStudentInfo13(""); setLocation13(""); setAchievement13(""); setDiplomaSerial13(""); setGuidanceFile13(null);
    setName14(""); setStudentInfo14(""); setLocation14(""); setAchievement14(""); setDiplomaSerial14(""); setGuidanceFile14(null);
    setName16(""); setPublishTime16(""); setAuthorsCount16(""); setForeignAuthors16(""); setLanguage16(""); setCustomLanguage16(""); setPublishParams16(""); setDirection16(""); setPermissionFile16(null);
    setName17(""); setPublishTime17(""); setAuthorsCount17(""); setForeignAuthors17(""); setLanguage17(""); setCustomLanguage17(""); setPublishParams17(""); setDirection17(""); setPermissionFile17(null);
    setCertType18(""); setLevel18(""); setDate18(""); setCertFile18(null);
    setType19(""); setLocation19(""); setStartDate19(""); setEndDate19(""); setDirection19(""); setPurpose19(""); setDocFile19(null);
    setDegreeType21(""); setDiplomaSerial21(""); setDiplomaNumber21(""); setDiplomaDate21(""); setSpecialty21("");
    setSpecialtyCode22(""); setSpecialtyName22(""); setTitleDate22(""); setTitleSerial22("");
    setStudentName23(""); setStudentPosition23(""); setOrganization23(""); setDefenseDate23(""); setSpecialtyCode23("");
    setJournalName24(""); setDatabase24(""); setArticleName24(""); setPublishDate24(""); setCountry24(""); setAuthorsCount24(""); setArticleLang24(""); setCustomArticleLang24(""); setPublishParams24(""); setProfileUrl24("");
    setJournalName25(""); setOakRegistry25(""); setArticleName25(""); setPublishDate25(""); setCountry25(""); setAuthorsCount25(""); setArticleLang25(""); setCustomArticleLang25(""); setPublishParams25(""); setArticleUrl25("");
    setProfileUrl26(""); setDatabase26(""); setArticleCount26(""); setHIndex26("");
    setJournalName27(""); setOakRegistry27(""); setArticleName27(""); setPublishDate27(""); setAuthorsCount27(""); setArticleLang27(""); setCustomArticleLang27(""); setPublishParams27(""); setArticleUrl27("");
    setPatentName28(""); setPatentType28(""); setPatentDate28(""); setRegistrationNumber28(""); setOwnersCount28("");
    setProjectName29(""); setContractDate29(""); setTotalAmount29(""); setParticipantsCount29(""); setReceivedAmount29("");
    setGrantName210(""); setGrantType210(""); setProjectNumber210(""); setContractNumber210(""); setProjectDuration210(""); setTotalSum210(""); setParticipantsCount210("");
    setCouncilName211(""); setOrganizationName211(""); setSpecialty211(""); setPosition211(""); setMembershipDate211("");
    setForeignCountry31(""); setForeignUniversity31(""); setDegreeDate31(""); setDegreeLevel31(""); setDiplomaSerial31(""); setSpecialtyName31("");
    setForeignCountry32(""); setForeignUniversity32(""); setSpecialtyName32(""); setLectureType32(""); setDuration32("");
    setTrainingType33(""); setTrainingLocation33(""); setTrainingDate33(""); setTrainingDirection33(""); setTrainingPurpose33("");
    setCooperationName34(""); setCooperationLocation34(""); setCooperationDate34(""); setCooperationDirection34(""); setCooperationPurpose34("");
    setEventName41(""); setEventDate41(""); setEventLocation41("");
    setClubName42(""); setClubRegulations42("");
    setActivityName43(""); setActivityDate43(""); setActivityLocation43("");
    setProjectName44(""); setProjectDate44(""); setProjectLocation44("");
    setCompetitionName45(""); setCompetitionType45(""); setStudentFish45(""); setCompetitionLocation45(""); setCompetitionDate45(""); setAchievement45(""); setDiplomaSerial45("");
    setMediaSubject46(""); setMediaDate46(""); setMediaName46("");
    setName47(""); setTime47(""); setReport47("");
  };

  const editApp = (app) => {
    setEditId(app.id);
    setShowForm(true);

    // Set existing files
    if (app.files && app.files.length > 0) {
      setExistingFiles(app.files);
    }

    // Scroll to form after state updates
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);

    if (is11) {
      try {
        const data = JSON.parse(app.work || '{}');
        setName11(data.name || "");
        setAuthorsCount11(data.authorsCount || "");
        setApprovedDate11(data.approvedDate || "");
        setPlatformName11(data.platformName || "");
        setInternetAddress11(data.internetAddress || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is12) {
      try {
        const data = JSON.parse(app.work || '{}');
        setEducationDirection12(data.educationDirection || "");
        setGroups12(data.groups || "");
        setTotalStudents12(data.totalStudents || "");
        setMonitoringStudents12(data.monitoringStudents || "");
        setAverageScore12(data.averageScore || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is13) {
      try {
        const data = JSON.parse(app.work || '{}');
        setName13(data.name || "");
        setStudentInfo13(data.studentInfo || "");
        setLocation13(data.location || "");
        setAchievement13(data.achievement || "");
        setDiplomaSerial13(data.diplomaSerial || "");
        setGuidanceFile13(null); // Reset file on edit
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is14) {
      try {
        const data = JSON.parse(app.work || '{}');
        setName14(data.name || "");
        setStudentInfo14(data.studentInfo || "");
        setLocation14(data.location || "");
        setAchievement14(data.achievement || "");
        setDiplomaSerial14(data.diplomaSerial || "");
        setGuidanceFile14(null); // Reset file on edit
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is16) {
      try {
        const data = JSON.parse(app.work || '{}');
        setName16(data.name || "");
        setPublishTime16(data.publishTime || "");
        setAuthorsCount16(data.authorsCount || "");
        setForeignAuthors16(data.foreignAuthors || "");
        setLanguage16(data.language || "");
        setPublishParams16(data.publishParams || "");
        setDirection16(data.direction || "");
        setPermissionFile16(null); // Reset file on edit
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is17) {
      try {
        const data = JSON.parse(app.work || '{}');
        setName17(data.name || "");
        setPublishTime17(data.publishTime || "");
        setAuthorsCount17(data.authorsCount || "");
        setForeignAuthors17(data.foreignAuthors || "");
        // Check if language is a predefined option or custom
        if (data.language && !LANGUAGE_OPTIONS.includes(data.language)) {
          setLanguage17("Boshqa");
          setCustomLanguage17(data.language);
        } else {
          setLanguage17(data.language || "");
          setCustomLanguage17("");
        }
        setPublishParams17(data.publishParams || "");
        setDirection17(data.direction || "");
        setPermissionFile17(null); // Reset file on edit
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is18) {
      try {
        const data = JSON.parse(app.work || '{}');
        setCertType18(data.certType || "");
        setLevel18(data.level || "");
        setDate18(data.date || "");
        setCertFile18(null); // Reset file on edit
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is19) {
      try {
        const data = JSON.parse(app.work || '{}');
        setType19(data.type || "");
        setLocation19(data.location || "");
        setStartDate19(data.startDate || "");
        setEndDate19(data.endDate || "");
        setDirection19(data.direction || "");
        setPurpose19(data.purpose || "");
        setDocFile19(null); // Reset file on edit
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is21) {
      try {
        const data = JSON.parse(app.work || '{}');
        setDegreeType21(data.degreeType || "");
        setDiplomaSerial21(data.diplomaSerial || "");
        setDiplomaNumber21(data.diplomaNumber || "");
        setDiplomaDate21(data.diplomaDate || "");
        setSpecialty21(data.specialty || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is22) {
      try {
        const data = JSON.parse(app.work || '{}');
        setSpecialtyCode22(data.specialtyCode || "");
        setSpecialtyName22(data.specialtyName || "");
        setTitleDate22(data.titleDate || "");
        setTitleSerial22(data.titleSerial || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is23) {
      try {
        const data = JSON.parse(app.work || '{}');
        setStudentName23(data.studentName || "");
        setStudentPosition23(data.studentPosition || "");
        setOrganization23(data.organization || "");
        setDefenseDate23(data.defenseDate || "");
        setSpecialtyCode23(data.specialtyCode || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is24) {
      try {
        const data = JSON.parse(app.work || '{}');
        setJournalName24(data.journalName || "");
        setDatabase24(data.database || "");
        setArticleName24(data.articleName || "");
        setPublishDate24(data.publishDate || "");
        setCountry24(data.country || "");
        setAuthorsCount24(data.authorsCount || "");
        setForeignAuthors24(data.foreignAuthors || "");
        // Check if language is a predefined option or custom
        if (data.articleLang && !LANGUAGE_OPTIONS.includes(data.articleLang)) {
          setArticleLang24("Boshqa");
          setCustomArticleLang24(data.articleLang);
        } else {
          setArticleLang24(data.articleLang || "");
          setCustomArticleLang24("");
        }
        setPublishParams24(data.publishParams || "");
        setProfileUrl24(data.profileUrl || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is25) {
      try {
        const data = JSON.parse(app.work || '{}');
        setJournalName25(data.journalName || "");
        setOakRegistry25(data.oakRegistry || "");
        setArticleName25(data.articleName || "");
        setPublishDate25(data.publishDate || "");
        setCountry25(data.country || "");
        setAuthorsCount25(data.authorsCount || "");
        setForeignAuthors25(data.foreignAuthors || "");
        // Check if language is a predefined option or custom
        if (data.articleLang && !LANGUAGE_OPTIONS.includes(data.articleLang)) {
          setArticleLang25("Boshqa");
          setCustomArticleLang25(data.articleLang);
        } else {
          setArticleLang25(data.articleLang || "");
          setCustomArticleLang25("");
        }
        setPublishParams25(data.publishParams || "");
        setArticleUrl25(data.articleUrl || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is26) {
      try {
        const data = JSON.parse(app.work || '{}');
        setDatabase26(data.database || "");
        setProfileUrl26(data.profileUrl || "");
        setArticleCount26(data.articleCount || "");
        setHIndex26(data.hIndex || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is27) {
      try {
        const data = JSON.parse(app.work || '{}');
        setJournalName27(data.journalName || "");
        setOakRegistry27(data.oakRegistry || "");
        setArticleName27(data.articleName || "");
        setPublishDate27(data.publishDate || "");
        setAuthorsCount27(data.authorsCount || "");
        setForeignAuthors27(data.foreignAuthors || "");
        // Check if language is a predefined option or custom
        if (data.articleLang && !LANGUAGE_OPTIONS.includes(data.articleLang)) {
          setArticleLang27("Boshqa");
          setCustomArticleLang27(data.articleLang);
        } else {
          setArticleLang27(data.articleLang || "");
          setCustomArticleLang27("");
        }
        setPublishParams27(data.publishParams || "");
        setArticleUrl27(data.articleUrl || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is28) {
      try {
        const data = JSON.parse(app.work || '{}');
        setPatentName28(data.patentName || "");
        setPatentType28(data.patentType || "");
        setPatentDate28(data.patentDate || "");
        setRegistrationNumber28(data.registrationNumber || "");
        setOwnersCount28(data.ownersCount || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is29) {
      try {
        const data = JSON.parse(app.work || '{}');
        setProjectName29(data.projectName || "");
        setContractDate29(data.contractDate || "");
        setTotalAmount29(data.totalAmount || "");
        setParticipantsCount29(data.participantsCount || "");
        setReceivedAmount29(data.receivedAmount || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is210) {
      try {
        const data = JSON.parse(app.work || '{}');
        setGrantName210(data.grantName || "");
        setGrantType210(data.grantType || "");
        setProjectNumber210(data.projectNumber || "");
        setContractNumber210(data.contractNumber || "");
        setProjectDuration210(data.projectDuration || "");
        setTotalSum210(data.totalSum || "");
        setParticipantsCount210(data.participantsCount || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is211) {
      try {
        const data = JSON.parse(app.work || '{}');
        setCouncilName211(data.councilName || "");
        setOrganizationName211(data.organizationName || "");
        setSpecialty211(data.specialty || "");
        setPosition211(data.position || "");
        setMembershipDate211(data.membershipDate || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is31) {
      try {
        const data = JSON.parse(app.work || '{}');
        setForeignCountry31(data.foreignCountry || "");
        setForeignUniversity31(data.foreignUniversity || "");
        setDegreeDate31(data.degreeDate || "");
        setDegreeLevel31(data.degreeLevel || "");
        setDiplomaSerial31(data.diplomaSerial || "");
        setSpecialtyName31(data.specialtyName || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is32) {
      try {
        const data = JSON.parse(app.work || '{}');
        setForeignCountry32(data.foreignCountry || "");
        setForeignUniversity32(data.foreignUniversity || "");
        setSpecialtyName32(data.specialtyName || "");
        setLectureType32(data.lectureType || "");
        setDuration32(data.duration || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is33) {
      try {
        const data = JSON.parse(app.work || '{}');
        setTrainingType33(data.trainingType || "");
        setTrainingLocation33(data.trainingLocation || "");
        setTrainingDate33(data.trainingDate || "");
        setTrainingDirection33(data.trainingDirection || "");
        setTrainingPurpose33(data.trainingPurpose || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is34) {
      try {
        const data = JSON.parse(app.work || '{}');
        setCooperationName34(data.cooperationName || "");
        setCooperationLocation34(data.cooperationLocation || "");
        setCooperationDate34(data.cooperationDate || "");
        setCooperationDirection34(data.cooperationDirection || "");
        setCooperationPurpose34(data.cooperationPurpose || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is41) {
      try {
        const data = JSON.parse(app.work || '{}');
        setEventName41(data.eventName || "");
        setEventDate41(data.eventDate || "");
        setEventLocation41(data.eventLocation || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is42) {
      try {
        const data = JSON.parse(app.work || '{}');
        setClubName42(data.clubName || "");
        setClubRegulations42(data.clubRegulations || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is43) {
      try {
        const data = JSON.parse(app.work || '{}');
        setActivityName43(data.activityName || "");
        setActivityDate43(data.activityDate || "");
        setActivityLocation43(data.activityLocation || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is44) {
      try {
        const data = JSON.parse(app.work || '{}');
        setProjectName44(data.projectName || "");
        setProjectDate44(data.projectDate || "");
        setProjectLocation44(data.projectLocation || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is45) {
      try {
        const data = JSON.parse(app.work || '{}');
        setCompetitionName45(data.competitionName || "");
        setCompetitionType45(data.competitionType || "");
        setStudentFish45(data.studentFish || "");
        setCompetitionLocation45(data.competitionLocation || "");
        setCompetitionDate45(data.competitionDate || "");
        setAchievement45(data.achievement || "");
        setDiplomaSerial45(data.diplomaSerial || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is46) {
      try {
        const data = JSON.parse(app.work || '{}');
        setMediaSubject46(data.mediaSubject || "");
        setMediaDate46(data.mediaDate || "");
        setMediaName46(data.mediaName || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else if (is47) {
      try {
        const data = JSON.parse(app.work || '{}');
        setName47(data.name || "");
        setTime47(data.time || "");
        setReport47(data.report || "");
      } catch (e) {
        setDesc(app.work || "");
      }
    } else {
      setDesc(app.work || "");
    }
  };

  const deleteApp = (appId) => {
    if (window.confirm('Bu arizani o\'chirmoqchimisiz? Bu amalni ortga qaytarib bo\'lmaydi.')) {
      Axios().delete(`api/v1/application/delete/${appId}/`)
        .then(res => {
          loadMyApps(); // Refresh the list
        })
        .catch(e => {
          console.error('Delete error:', e);
          alert('Xatolik yuz berdi. Qaytadan urinib ko\'ring.');
        });
    } else {
    }
  };

  const renderWork = (app) => {
    const appParamCode = app?.parameter?.code;

    if (String(appParamCode) === '1.1') {
      try {
        const d = JSON.parse(app.work || '{}');
        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              <Text fontWeight={600}>Nomi</Text>
              <Text>{d?.name || '-'}</Text>
              <Text fontWeight={600}>Mualliflar soni</Text>
              <Text>{d?.authorsCount || '-'}</Text>
              <Text fontWeight={600}>Tasdiqlangan sana</Text>
              <Text>{d?.approvedDate || '-'}</Text>
              <Text fontWeight={600}>Platforma nomi</Text>
              <Text>{d?.platformName || '-'}</Text>
              <Text fontWeight={600}>Internet manzili</Text>
              {d?.internetAddress ? (
                <Text
                  color="blue.600"
                  textDecoration="underline"
                  cursor="pointer"
                  onClick={() => {
                    const url = d.internetAddress.startsWith('http') ? d.internetAddress : `https://${d.internetAddress}`;
                    window.open(url, '_blank');
                  }}
                >
                  {d.internetAddress}
                </Text>
              ) : (
                <Text>-</Text>
              )}
            </Box>
          </Box>
        );
      } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '1.2') {
      try {
        const d = JSON.parse(app.work || '{}');
        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              <Text fontWeight={600}>Ta'lim yo'nalishi</Text>
              <Text>{d?.educationDirection || '-'}</Text>
              <Text fontWeight={600}>Guruhlar</Text>
              <Text>{d?.groups || '-'}</Text>
              <Text fontWeight={600}>Jami talabalar soni</Text>
              <Text>{d?.totalStudents || '-'}</Text>
              <Text fontWeight={600}>Monitoring talabalar soni</Text>
              <Text>{d?.monitoringStudents || '-'}</Text>
              <Text fontWeight={600}>O'rtacha o'zlashtirish</Text>
              <Text>{d?.averageScore || '-'}</Text>
            </Box>
          </Box>
        );
      } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '1.3') {
      try {
        const d = JSON.parse(app.work || '{}');
        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              <Text fontWeight={600}>Nomi va turi</Text>
              <Text>{d?.name || '-'}</Text>
              <Text fontWeight={600}>Talabaning FISh</Text>
              <Text>{d?.studentInfo || '-'}</Text>
              <Text fontWeight={600}>Joyi, sanasi</Text>
              <Text>{d?.location || '-'}</Text>
              <Text fontWeight={600}>Egallagan o'rni</Text>
              <Text>{d?.achievement || '-'}</Text>
              <Text fontWeight={600}>Diplom seriyasi</Text>
              <Text>{d?.diplomaSerial || '-'}</Text>
            </Box>
          </Box>
        );
      } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '1.4') {
      try {
        const d = JSON.parse(app.work || '{}');
        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              <Text fontWeight={600}>Nomi va turi</Text>
              <Text>{d?.name || '-'}</Text>
              <Text fontWeight={600}>Talabaning FISh</Text>
              <Text>{d?.studentInfo || '-'}</Text>
              <Text fontWeight={600}>Joyi, sanasi</Text>
              <Text>{d?.location || '-'}</Text>
              <Text fontWeight={600}>Egallagan o'rni</Text>
              <Text>{d?.achievement || '-'}</Text>
              <Text fontWeight={600}>Diplom seriyasi</Text>
              <Text>{d?.diplomaSerial || '-'}</Text>
            </Box>
          </Box>
        );
      } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '1.6') {
      try {
        const d = JSON.parse(app.work || '{}');
        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              <Text fontWeight={600}>Nomi</Text>
              <Text>{d?.name || '-'}</Text>
              <Text fontWeight={600}>Nashr etilgan vaqti</Text>
              <Text>{d?.publishTime || '-'}</Text>
              <Text fontWeight={600}>Mualliflar soni</Text>
              <Text>{d?.authorsCount || '-'}</Text>
              <Text fontWeight={600}>Xorijlik mualliflar soni</Text>
              <Text>{d?.foreignAuthors || '-'}</Text>
              <Text fontWeight={600}>Tili</Text>
              <Text>{d?.language || '-'}</Text>
              <Text fontWeight={600}>Nashr parametlari (nashriyot, sahifasi)</Text>
              <Text>{d?.publishParams || '-'}</Text>
              <Text fontWeight={600}>Turi</Text>
              <Text>{d?.direction || '-'}</Text>
            </Box>
          </Box>
        );
      } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '1.7') {
      try {
        const d = JSON.parse(app.work || '{}');
        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              <Text fontWeight={600}>Nomi</Text>
              <Text>{d?.name || '-'}</Text>
              <Text fontWeight={600}>Nashr etilgan vaqti</Text>
              <Text>{d?.publishTime || '-'}</Text>
              <Text fontWeight={600}>Mualliflar soni</Text>
              <Text>{d?.authorsCount || '-'}</Text>
              <Text fontWeight={600}>Xorijlik mualliflar soni</Text>
              <Text>{d?.foreignAuthors || '-'}</Text>
              <Text fontWeight={600}>Tili</Text>
              <Text>{d?.language || '-'}</Text>
              <Text fontWeight={600}>Nashr parametlari</Text>
              <Text>{d?.publishParams || '-'}</Text>
              <Text fontWeight={600}>Yo'nalishi</Text>
              <Text>{d?.direction || '-'}</Text>
            </Box>
          </Box>
        );
      } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '1.8') {
      try {
        const d = JSON.parse(app.work || '{}');
        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              <Text fontWeight={600}>Sertifikat turi</Text>
              <Text>{d?.certType || '-'}</Text>
              <Text fontWeight={600}>Daraja</Text>
              <Text>{d?.level || '-'}</Text>
              <Text fontWeight={600}>Sana</Text>
              <Text>{d?.date || '-'}</Text>
            </Box>
          </Box>
        );
      } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '1.9') {
      try {
        const d = JSON.parse(app.work || '{}');
        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              <Text fontWeight={600}>Tur</Text>
              <Text>{d?.type || '-'}</Text>
              <Text fontWeight={600}>Joyi</Text>
              <Text>{d?.location || '-'}</Text>
              <Text fontWeight={600}>Boshlanish sanasi</Text>
              <Text>{d?.startDate || '-'}</Text>
              <Text fontWeight={600}>Tugash sanasi</Text>
              <Text>{d?.endDate || '-'}</Text>
              <Text fontWeight={600}>Yo'nalishi</Text>
              <Text>{d?.direction || '-'}</Text>
              <Text fontWeight={600}>Maqsadi</Text>
              <Text>{d?.purpose || '-'}</Text>
            </Box>
          </Box>
        );
      } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '2.1') {
      try {
        const d = JSON.parse(app.work || '{}');
        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              <Text fontWeight={600}>Daraja turi</Text>
              <Text>{d?.degreeType || '-'}</Text>
              <Text fontWeight={600}>Diplom seriyasi</Text>
              <Text>{d?.diplomaSerial || '-'}</Text>
              <Text fontWeight={600}>Diplom raqami</Text>
              <Text>{d?.diplomaNumber || '-'}</Text>
              <Text fontWeight={600}>Berilgan sana</Text>
              <Text>{d?.diplomaDate || '-'}</Text>
              <Text fontWeight={600}>Ixtisoslik</Text>
              <Text>{d?.specialty || '-'}</Text>
            </Box>
          </Box>
        );
      } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '2.2') {
      try {
        const d = JSON.parse(app.work || '{}');
        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              <Text fontWeight={600}>Mutaxassislik kodi</Text>
              <Text>{d?.specialtyCode || '-'}</Text>
              <Text fontWeight={600}>Mutaxassislik nomi</Text>
              <Text>{d?.specialtyName || '-'}</Text>
              <Text fontWeight={600}>Unvon berilgan sana</Text>
              <Text>{d?.titleDate || '-'}</Text>
              <Text fontWeight={600}>Unvon guvohnomasi seriyasi</Text>
              <Text>{d?.titleSerial || '-'}</Text>
            </Box>
          </Box>
        );
      } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '2.3') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Shogirdining FISHi', value: d?.studentName || d?.pupilFullName || d?.pupilName23 },
          { label: 'Shogirdining lavozimi', value: d?.studentPosition || d?.pupilPosition || d?.pupilPosition23 },
          { label: 'Ish bajarilgan tashkilot', value: d?.organization || d?.org23 },
          { label: 'Himoya sanasi', value: d?.defenseDate || d?.defenseDate23 },
          { label: 'Ixtisosligi shifri va nomi', value: d?.specialtyCode && d?.specialtyName ? `${d.specialtyCode} - ${d.specialtyName}` : (d?.specialtyCodeName || d?.specCodeName23 || d?.specialtyCode || d?.specialtyName) }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        console.error('Error parsing KPI 2.3 data:', e, 'work:', app?.work);
        return <Text>{app?.work}</Text>;
      }
    } else if (String(appParamCode) === '2.4') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Jurnal nomi', value: d?.journalName },
          { label: 'Xalqaro ilmiy-texnik baza nomi', value: d?.database },
          { label: 'Maqolaning nomi', value: d?.articleName },
          { label: 'Nashr etilgan vaqti', value: d?.publishDate },
          { label: 'Maqola chop etilgan davlat', value: d?.country },
          { label: 'Mualliflar soni', value: d?.authorsCount },
          { label: 'Xorijlik mualliflar soni', value: d?.foreignAuthors },
          { label: 'Maqolaning chop etilgan tili', value: d?.articleLang },
          { label: 'Nashr parametrlari (jurnal soni, sahifasi)', value: d?.publishParams },
          { label: 'Profil manzili', value: d?.profileUrl }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  {renderFieldValue(field)}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '2.5') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Jurnal nomi', value: d?.journalName },
          { label: 'OAK ro\'yxati', value: d?.oakRegistry },
          { label: 'Maqolaning nomi', value: d?.articleName },
          { label: 'Nashr etilgan vaqti', value: d?.publishDate },
          { label: 'Maqola chop etilgan davlat', value: d?.country },
          { label: 'Mualliflar soni', value: d?.authorsCount },
          { label: 'Xorijlik mualliflar soni', value: d?.foreignAuthors },
          { label: 'Maqolaning chop etilgan tili', value: d?.articleLang },
          { label: 'Nashr parametrlari (jurnal soni, sahifasi)', value: d?.publishParams },
          { label: 'Maqola manzili', value: d?.articleUrl }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  {renderFieldValue(field)}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '2.6') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Ma\'lumot bazasi', value: d?.database },
          { label: 'Profil manzili', value: d?.profileUrl },
          { label: 'Maqolalar soni', value: d?.articleCount },
          { label: 'H-indeks', value: d?.hIndex }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  {renderFieldValue(field)}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        console.error('Error parsing KPI 2.6 data:', e, 'work:', app?.work);
        return <Text>{app?.work}</Text>;
      }
    } else if (String(appParamCode) === '2.7') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Jurnal nomi', value: d?.journalName },
          { label: 'OAC ro\'yxati', value: d?.oakRegistry },
          { label: 'Maqolaning nomi', value: d?.articleName },
          { label: 'Nashr etilgan vaqti', value: d?.publishDate },
          { label: 'Mualliflar soni', value: d?.authorsCount },
          { label: 'Xorijlik mualliflar soni', value: d?.foreignAuthors },
          { label: 'Maqolaning chop etilgan tili', value: d?.articleLang },
          { label: 'Nashr parametrlari', value: d?.publishParams },
          { label: 'Maqola manzili', value: d?.articleUrl }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  {renderFieldValue(field)}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        console.error('Error parsing KPI 2.7 data:', e, 'work:', app?.work);
        return <Text>{app?.work}</Text>;
      }
    } else if (String(appParamCode) === '2.8') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Patent nomi', value: d?.patentName },
          { label: 'Patent turi', value: d?.patentType },
          { label: 'Patent sanasi', value: d?.patentDate },
          { label: 'Ro\'yxatga olish raqami', value: d?.registrationNumber },
          { label: 'Egalar soni', value: d?.ownersCount }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        console.error('Error parsing KPI 2.8 data:', e, 'work:', app?.work);
        return <Text>{app?.work}</Text>;
      }
    } else if (String(appParamCode) === '2.9') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Loyiha nomi', value: d?.projectName },
          { label: 'Shartnoma sanasi', value: d?.contractDate },
          { label: 'Umumiy summa', value: d?.totalAmount },
          { label: 'Ishtirokchilar soni', value: d?.participantsCount },
          { label: 'Olingan summa', value: d?.receivedAmount }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        console.error('Error parsing KPI 2.9 data:', e, 'work:', app?.work);
        return <Text>{app?.work}</Text>;
      }
    } else if (String(appParamCode) === '2.10') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Grant nomi', value: d?.grantName },
          { label: 'Grant turi', value: d?.grantType },
          { label: 'Loyiha raqami', value: d?.projectNumber },
          { label: 'Shartnoma raqami', value: d?.contractNumber },
          { label: 'Loyiha davomiyligi', value: d?.projectDuration },
          { label: 'Umumiy summa', value: d?.totalSum },
          { label: 'Ishtirokchilar soni', value: d?.participantsCount }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        console.error('Error parsing KPI 2.10 data:', e, 'work:', app?.work);
        return <Text>{app?.work}</Text>;
      }
    } else if (String(appParamCode) === '2.11') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Kengash nomi', value: d?.councilName },
          { label: 'Tashkilot nomi', value: d?.organizationName },
          { label: 'Mutaxassislik', value: d?.specialty },
          { label: 'Lavozim', value: d?.position },
          { label: 'A\'zolik sanasi', value: d?.membershipDate }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        console.error('Error parsing KPI 2.11 data:', e, 'work:', app?.work);
        return <Text>{app?.work}</Text>;
      }
    } else if (String(appParamCode) === '3.1') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Xorijiy davlat', value: d?.foreignCountry },
          { label: 'Xorijiy OTM nomi', value: d?.foreignUniversity },
          { label: 'Vaqti', value: d?.degreeDate },
          { label: 'Ilmiy daraja bosqichi (PhD/DSc)', value: d?.degreeLevel },
          { label: 'Diplomining seriyasi va raqami', value: d?.diplomaSerial },
          { label: 'Ixtisosligi nomi', value: d?.specialtyName }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        console.error('Error parsing KPI 3.1 data:', e, 'work:', app?.work);
        return <Text>{app?.work}</Text>;
      }
    } else if (String(appParamCode) === '3.2') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Xorijiy davlat nomi', value: d?.foreignCountry },
          { label: 'Xorijiy OTM nomi', value: d?.foreignUniversity },
          { label: 'Ixtisosligi nomi', value: d?.specialtyName },
          { label: 'O\‘quv mashg‘ulot turi', value: d?.lectureType },
          { label: 'Vaqti (boshlanishi va tugashi (kalendar))', value: d?.duration }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        console.error('Error parsing KPI 3.2 data:', e, 'work:', app?.work);
        return <Text>{app?.work}</Text>;
      }
    } else if (String(appParamCode) === '3.3') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Turi', value: d?.trainingType },
          { label: 'Joyi', value: d?.trainingLocation },
          { label: 'Vaqti', value: d?.trainingDate },
          { label: 'Yo‘nalish', value: d?.trainingDirection },
          { label: 'Maqsadi', value: d?.trainingPurpose }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '3.4') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Nomi', value: d?.cooperationName },
          { label: 'Joyi', value: d?.cooperationLocation },
          { label: 'Vaqti', value: d?.cooperationDate },
          { label: 'Yo‘nalish', value: d?.cooperationDirection },
          { label: 'Maqsadi', value: d?.cooperationPurpose }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '4.1') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Nomi', value: d?.eventName },
          { label: 'Vaqti', value: d?.eventDate },
          { label: 'Joy', value: d?.eventLocation }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '4.3') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Nomi', value: d?.activityName },
          { label: 'Vaqti', value: d?.activityDate },
          { label: 'Joy', value: d?.activityLocation }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '4.4') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Nomi', value: d?.projectName },
          { label: 'Vaqti', value: d?.projectDate },
          { label: 'Joy', value: d?.projectLocation }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '4.2') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Nomi', value: d?.clubName },
          { label: 'Nizomi', value: d?.clubRegulations }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '4.5') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Nomi', value: d?.competitionName },
          { label: 'Turi', value: d?.competitionType },
          { label: 'Talabaning FISHi', value: d?.studentFish },
          { label: 'Joy', value: d?.competitionLocation },
          { label: 'Vaqti', value: d?.competitionDate },
          { label: 'Egallagan o\'rni', value: d?.achievement },
          { label: 'Diplom seriyasi va raqami', value: d?.diplomaSerial }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '4.6') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Mavzu', value: d?.mediaSubject },
          { label: 'Vaqti', value: d?.mediaDate },
          { label: 'OAV, ijtimoiy tarmoq', value: d?.mediaName }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '4.7') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Nomi', value: d?.name },
          { label: 'Vaqti', value: d?.time },
          { label: 'Hisobot', value: d?.report }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) { return <Text>{app?.work}</Text>; }
    } else {
      return <Text whiteSpace="pre-wrap">{app?.work}</Text>;
    }
  };

  // Check if application is a kafedra-based point assignment
  const isKafedraPoint = (app) => {
    const workText = String(app?.work || '');
    // Check for kafedra point indicators (both old text format and new JSON format)
    return workText.includes("Bo'lim boshlig'i:") ||
      workText.includes("Bo'lim boshlig'i tomonidan berildi (kafedra bahosi)") ||
      workText.includes("Kafedra:") ||
      workText.includes('"type": "kafedra_score"') ||
      workText.includes('"type":"kafedra_score"');
  };

  // Check if application is KPI 2.1 (informational display only)
  const isKPI21 = (app) => {
    return String(app?.parameter?.code) === '2.1';
  };

  // Render KPI 2.1 informational card (no approve/reject buttons)
  const renderKPI21Card = (app) => {
    try {
      const data = JSON.parse(app.work || '{}');

      return (
        <Box
          p={4}
          mb={3}
          borderRadius={8}
          bg="linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)"
          border="2px solid #3b82f6"
          position="relative"
          overflow="hidden"
          boxShadow="0 4px 12px rgba(59, 130, 246, 0.15)"
        >
          {/* Decorative elements */}
          <Box
            position="absolute"
            top="-10px"
            right="-10px"
            width="60px"
            height="60px"
            bg="blue.200"
            borderRadius="50%"
            opacity={0.3}
          />
          <Box
            position="absolute"
            bottom="-15px"
            left="-15px"
            width="40px"
            height="40px"
            bg="blue.300"
            borderRadius="50%"
            opacity={0.2}
          />

          <Box position="relative" zIndex={2}>
            <Box mb={3}>
              <Text fontWeight={700} color="blue.900" fontSize="lg" mb={1}>
                Ma'lumot yuborildi
              </Text>
              <Box height="2px" bg="blue.500" borderRadius="1px" width="50px"></Box>
            </Box>

            {/* Application details */}
            <Box
              bg="white"
              p={3}
              borderRadius={6}
              mb={2}
              border="1px solid #3b82f6"
              boxShadow="0 2px 6px rgba(0, 0, 0, 0.08)"
            >
              <Box display="grid" gridTemplateColumns="200px 1fr" gap={2}>
                {data.degreeType && (
                  <>
                    <Text fontWeight={600} fontSize="sm" color="gray.700">Daraja turi:</Text>
                    <Text fontSize="sm">{data.degreeType}</Text>
                  </>
                )}
                {data.diplomaSerial && (
                  <>
                    <Text fontWeight={600} fontSize="sm" color="gray.700">Diplom seriyasi:</Text>
                    <Text fontSize="sm">{data.diplomaSerial}</Text>
                  </>
                )}
                {data.diplomaNumber && (
                  <>
                    <Text fontWeight={600} fontSize="sm" color="gray.700">Diplom raqami:</Text>
                    <Text fontSize="sm">{data.diplomaNumber}</Text>
                  </>
                )}
                {data.issueDate && (
                  <>
                    <Text fontWeight={600} fontSize="sm" color="gray.700">Berilgan sana:</Text>
                    <Text fontSize="sm">{data.issueDate}</Text>
                  </>
                )}
                {data.specialty && (
                  <>
                    <Text fontWeight={600} fontSize="sm" color="gray.700">Ixtisoslik:</Text>
                    <Text fontSize="sm">{data.specialty}</Text>
                  </>
                )}
              </Box>

              {/* Files */}
              {app.files && app.files.length > 0 && (
                <Box mt={3} pt={3} borderTop="1px solid #e5e7eb">
                  <Text fontWeight={600} fontSize="sm" color="gray.700" mb={2}>Fayllar:</Text>
                  {app.files.map((file, idx) => (
                    <Box key={idx} mb={1}>
                      <a
                        href={`${FILE_URL}${file.file}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#3b82f6', textDecoration: 'underline', fontSize: '14px' }}
                      >
                        📄 {file.name || 'Fayl'}
                      </a>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Box mt={2} pt={2} borderTop="1px solid #3b82f6">
              <Text fontSize="xs" color="blue.700" fontWeight={600}>Holat</Text>
              <Text fontSize="sm" color="blue.900" fontWeight={600}>
                {statusText(app.status, app)}
              </Text>
              {/* Show department-specific comments */}
              {app.tt_comment && (
                <Text fontSize="sm" color={app.tt_status === 2 ? 'red.600' : 'green.600'} fontStyle="italic" mt={1}>
                  Tarmoq texnologiyalari: {app.tt_comment}
                </Text>
              )}
              {app.ts_comment && (
                <Text fontSize="sm" color={app.ts_status === 2 ? 'red.600' : 'green.600'} fontStyle="italic" mt={1}>
                  Ta'lim sifati: {app.ts_comment}
                </Text>
              )}
              {app.ov_comment && (
                <Text fontSize="sm" color={app.ov_status === 2 ? 'red.600' : 'green.600'} fontStyle="italic" mt={1}>
                  O'quv bo'limi: {app.ov_comment}
                </Text>
              )}
              {app.mn_comment && (
                <Text fontSize="sm" color={app.mn_status === 2 ? 'red.600' : 'green.600'} fontStyle="italic" mt={1}>
                  Ma'naviyat bo'limi: {app.mn_comment}
                </Text>
              )}
              <Text fontSize="xs" color="gray.500" mt={1}>
                {new Date(app.created_at).toLocaleDateString('uz-UZ')}
              </Text>
            </Box>
          </Box>
        </Box>
      );
    } catch (e) {
      console.error('Error rendering KPI 2.1 card:', e);
      return (
        <Box p={3} mb={3} borderRadius={8} bg="gray.50" border="1px solid #e2e8f0">
          <Text>{app.work}</Text>
        </Box>
      );
    }
  };

  // Render kafedra point confirmation card
  const renderKafedraPointCard = (app) => {
    // Extract parameter code and additional data from work description
    const workText = app?.work || '';
    let paramCode = 'Noma\'lum';
    let percentage = null;
    let grade = null;
    let note = null;

    // Try to parse as JSON first (for KPI 1.2 and 1.5)
    try {
      const jsonData = JSON.parse(workText);
      if (jsonData.type === 'kafedra_score') {
        paramCode = jsonData.parameter_code || 'Noma\'lum';
        percentage = jsonData.percentage;
        grade = jsonData.grade;
        note = jsonData.note;
      }
    } catch (e) {
      // If not JSON, try to extract from old text format
      const paramCodeMatch = workText.match(/KPI ([\d.]+)/);
      paramCode = paramCodeMatch ? paramCodeMatch[1] : 'Noma\'lum';
    }

    // Get department name from the application
    let departmentName = app.partition__name || 'Noma\'lum bo\'lim';
    // Fix capitalization for "ilmiy bo'lim" to "Ilmiy bo'lim"
    if (departmentName.toLowerCase().includes('ilmiy bo\'lim')) {
      departmentName = departmentName.replace(/ilmiy bo'lim/i, 'Ilmiy bo\'lim');
    }

    return (
      <Box
        p={3}
        mb={3}
        borderRadius={8}
        bg="linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)"
        border="2px solid #22c55e"
        position="relative"
        overflow="hidden"
        boxShadow="0 4px 12px rgba(34, 197, 94, 0.15)"
      >
        {/* Decorative elements */}
        <Box
          position="absolute"
          top="-10px"
          right="-10px"
          width="60px"
          height="60px"
          bg="green.200"
          borderRadius="50%"
          opacity={0.3}
        />
        <Box
          position="absolute"
          bottom="-15px"
          left="-15px"
          width="40px"
          height="40px"
          bg="green.300"
          borderRadius="50%"
          opacity={0.2}
        />

        <Box position="relative" zIndex={2}>
          <Box mb={3}>
            <Text fontWeight={700} color="green.900" fontSize="lg" mb={1}>
              Bo'lim tomonidan berilgan ball
            </Text>
            <Box height="2px" bg="green.500" borderRadius="1px" width="50px"></Box>
          </Box>

          {/* Points awarded by department */}
          <Box
            bg="white"
            p={3}
            borderRadius={6}
            mb={2}
            border="1px solid #22c55e"
            boxShadow="0 2px 6px rgba(0, 0, 0, 0.08)"
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box flex={1}>
                <Text fontWeight={600} color="green.800" fontSize="sm" mb={1}>
                  {departmentName}
                </Text>
                <Text fontSize="xs" color="gray.600" mb={1}>
                  Bo'lim boshlig'i tomonidan tasdiqlangan
                </Text>
                <Text fontSize="xs" color="gray.500">
                  KPI {paramCode} parametri uchun
                </Text>
                {/* Show percentage for KPI 1.2 only */}
                {paramCode === '1.2' && percentage !== null && (
                  <Text fontSize="xs" color="blue.600" fontWeight={600} mt={2}>
                    O'zlashtirish ko'rsatkichi: {percentage}%
                  </Text>
                )}
                {/* Show grade for KPI 1.5 only */}
                {paramCode === '1.5' && grade !== null && (
                  <Text fontSize="xs" color="blue.600" fontWeight={600} mt={2}>
                    O'rtacha baho: {grade}
                  </Text>
                )}
              </Box>
              <Box textAlign="right" ml={3}>
                {!(paramCode === '2.5' && app.ball === 0) && (
                  <>
                    <Text fontWeight={800} fontSize="2xl" color="green.600" mb={1}>
                      {app.ball}
                    </Text>
                    <Text fontWeight={600} color="green.700" fontSize="xs">
                      ball
                    </Text>
                  </>
                )}
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {new Date(app.created_at).toLocaleDateString('uz-UZ')}
                </Text>
              </Box>
            </Box>
          </Box>

          {app.confirmed_user?.first_name && (
            <Box mt={2} pt={2} borderTop="1px solid #22c55e">
              <Text fontSize="xs" color="green.700" fontWeight={600}>Tasdiqlovchi</Text>
              <Text fontWeight={600} color="green.900" fontSize="sm">
                {formatFullName(app.confirmed_user.first_name, app.confirmed_user.last_name, app.confirmed_user.middle_name)}
              </Text>
            </Box>
          )}
        </Box>
      </Box>
    );
  };


  // Render application details based on parameter code
  const renderAppDetails = (app) => {
    const appParamCode = app?.parameter_code;

    if (isKafedraPoint(app)) {
      return renderKafedraPointCard(app);
    } else if (String(appParamCode).startsWith('1.')) {
      // Handle all 1.x parameters with generic field display
      try {
        const d = JSON.parse(app.work || '{}');

        // Map English field names to Uzbek
        const fieldLabelMap = {
          'profileUrl': 'Profil manzili',
          'articleUrl': 'Maqola manzili',
          'url': 'Manzil',
          'articleCount': 'Maqolalar soni',
          'authorsCount': 'Mualliflar soni',
          'foreignAuthors': 'Xorijlik mualliflar soni',
          'ownersCount': 'Egalar soni',
          'participantsCount': 'Ishtirokchilar soni',
          'name': 'Nomi',
          'eventName': 'Tadbir nomi',
          'clubName': 'To\'garak nomi',
          'activityName': 'Faoliyat nomi',
          'competitionName': 'Tanlov nomi',
          'projectName': 'Loyiha nomi',
          'type': 'Turi',
          'competitionType': 'Tanlov turi',
          'date': 'Sana',
          'location': 'Joy',
          'startDate': 'Boshlanish sanasi',
          'endDate': 'Tugash sanasi',
          'direction': 'Yo\'nalishi',
          'purpose': 'Maqsadi',
          'achievement': 'Egallagan o\'rni',
          'diplomaSerial': 'Diplom seriyasi va raqami',
          'articleLang': 'Maqolaning chop etilgan tili',
          'publishParams': 'Nashr parametrlari',
          'publishDate': 'Nashr etilgan vaqti',
          'country': 'Maqola chop etilgan davlat',
          'language': 'Tili',
          'certType': 'Sertifikat turi',
          'level': 'Daraja',
          'studentFish': 'Talabaning FISHi',
          'mediaSubject': 'Mavzu',
          'mediaName': 'OAV, ijtimoiy tarmoq',
          'time': 'Vaqti',
          'report': 'Hisobot',
          'oakRegistry': 'OAK ro\'yxati',
          'trainingType': 'Trening turi',
          'trainingLocation': 'Trening joyi',
          'trainingDate': 'Trening sanasi',
          'trainingDirection': 'Trening yo\'nalishi',
          'trainingPurpose': 'Trening maqsadi',
          'cooperationName': 'Hamkorlik nomi',
          'cooperationLocation': 'Hamkorlik joyi',
          'cooperationDate': 'Hamkorlik sanasi',
          'cooperationDirection': 'Hamkorlik yo\'nalishi',
          'cooperationPurpose': 'Hamkorlik maqsadi',
        };

        return (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {Object.keys(d).map((key, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{fieldLabelMap[key]}</Text>
                  <Text>{d[key]}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) { return <Text>{app?.work}</Text>; }
    }
  };

  // Check if any department has made a decision (approved or rejected)
  const hasAnyDepartmentDecision = (app) => {
    const code = app?.parameter?.code;

    // For multi-department parameters, check if any department has made a final decision (1=approved, 2=rejected)
    if (code === '1.1') {
      return app.tt_status === 1 || app.tt_status === 2 ||
        app.ts_status === 1 || app.ts_status === 2 ||
        app.ov_status === 1 || app.ov_status === 2;
    }
    if (code === '1.3' || code === '1.4') {
      return app.mn_status === 1 || app.mn_status === 2 ||
        app.ov_status === 1 || app.ov_status === 2;
    }
    if (code === '3.1') {
      return app.tt_status === 1 || app.tt_status === 2 ||
        app.ts_status === 1 || app.ts_status === 2;
    }
    // KPI 2.1 is informational only, no department approval
    if (code === '2.1') {
      return false;
    }
    if (code === '3.2' || code === '3.3') {
      return app.tt_status === 1 || app.tt_status === 2 ||
        app.ts_status === 1 || app.ts_status === 2 ||
        app.ov_status === 1 || app.ov_status === 2;
    }
    if (code === '3.4') {
      return app.tt_status === 1 || app.tt_status === 2 ||
        app.ts_status === 1 || app.ts_status === 2;
    }

    // For single-department parameters, check if status is approved (2, 3, 4)
    // Allow editing if status is Created (1), Not Considered (6), or Rejected (5)
    return app.status === 2 || app.status === 3 || app.status === 4; // Only block if approved
  };

  const renderDeptStripe = (app) => {
    try {
      const code = String(app?.parameter?.code || '');
      const items = [];
      const statusLabel = (v) => (v === 1 ? 'Tasdiqlandi' : v === 2 ? 'Rad etildi' : 'Jarayonda');
      if (code === '1.1') {
        items.push({ name: "Tarmoq texnologiyalari bo'limi", st: app.tt_status });
        items.push({ name: "Ta'lim sifati bo'limi", st: app.ts_status });
        items.push({ name: "O'quv bo'limi", st: app.ov_status });
      } else if (code === '1.3' || code === '1.4') {
        items.push({ name: "Ma'naviyat bo'limi", st: app.mn_status });
        items.push({ name: "O'quv bo'limi", st: app.ov_status });
      } else if (code === '1.6' || code === '1.7' || code === '1.8' || code === '1.9') {
        // For single-department parameters, use a different status display logic
        const singleDeptStatusLabel = (s) => {
          if (s === 5) return 'Rad etildi';  // Rejected
          if (s === 2 || s === 3 || s === 4) return 'Tasdiqlandi';  // Approved
          return 'Jarayonda';  // In progress
        };
        items.push({ name: "O'quv bo'limi", st: app.status, customLabel: singleDeptStatusLabel(app.status) });
      } else if (code === '3.1') {
        items.push({ name: "Xalqaro hamkorlik bo'limi", st: app.tt_status });
        items.push({ name: "Ilmiy bo'lim", st: app.ts_status });
      } else if (code === '3.2' || code === '3.3') {
        items.push({ name: "Xalqaro hamkorlik bo'limi", st: app.tt_status });
        items.push({ name: "Ilmiy bo'lim", st: app.ts_status });
        items.push({ name: "O'quv bo'limi", st: app.ov_status });
      } else if (code === '3.4') {
        items.push({ name: "Xalqaro hamkorlik bo'limi", st: app.tt_status });
        items.push({ name: "Ilmiy bo'lim", st: app.ts_status });
      } else if (code === '4.7') {
        // 4.7 is now single-department like other 4.x parameters → Ma'naviyat bo'limi
        const singleDeptStatusLabel = (s) => {
          if (s === 5) return 'Rad etildi';
          if (s === 2 || s === 3 || s === 4) return 'Tasdiqlandi';
          return 'Jarayonda';
        };
        items.push({ name: "Ma'naviyat bo'limi", st: app.status, customLabel: singleDeptStatusLabel(app.status) });

      } else if (code.startsWith('2.')) {
        // All other 2.x parameters (2.1–2.11) are single-department → Ilmiy tadqiqotlar
        // For 2.1, don't show status stripe (informational only)
        if (code !== '2.1') {
          const singleDeptStatusLabel = (s) => {
            if (s === 5) return 'Rad etildi';
            if (s === 2 || s === 3 || s === 4) return 'Tasdiqlandi';
            return 'Jarayonda';
          };
          items.push({ name: "Ilmiy bo'lim", st: app.status, customLabel: singleDeptStatusLabel(app.status) });
        }
      } else if (code.startsWith('4.')) {
        // All 4.x parameters are single-department → Ma'naviyat bo'limi
        const singleDeptStatusLabel = (s) => {
          if (s === 5) return 'Rad etildi';
          if (s === 2 || s === 3 || s === 4) return 'Tasdiqlandi';
          return 'Jarayonda';
        };
        items.push({ name: "Ma'naviyat bo'limi", st: app.status, customLabel: singleDeptStatusLabel(app.status) });
      } else {
        // Single department – show assigned partition if present
        if (app.partition__name) {
          items.push({ name: app.partition__name, st: app.status });
        }
      }
      if (!items.length) return null;

      return (
        <Box mt={2}>
          <Text fontWeight={600} fontSize="sm" color="gray.700" mb={2}>Natija:</Text>
          <Box display="flex" flexDirection="column" gap={2}>
            {items.map((it, idx) => {
              const status = it.customLabel || statusLabel(it.st);
              const isApproved = status === 'Tasdiqlandi';
              const isRejected = status === 'Rad etildi';
              const isInProgress = status === 'Jarayonda';

              // Set colors based on status
              let bgColor, borderColor, textColor;
              if (isApproved) {
                bgColor = 'green.50';
                borderColor = 'green.400';
                textColor = 'green.700';
              } else if (isRejected) {
                bgColor = 'red.50';
                borderColor = 'red.400';
                textColor = 'red.700';
              } else {
                // Jarayonda or any other status
                bgColor = 'yellow.50';
                borderColor = 'yellow.400';
                textColor = 'yellow.700';
              }

              // Get the comment for this specific department
              let deptComment = '';
              const paramCode = String(app?.parameter?.code || '');

              if (it.name === 'Tarmoq texnologiyalari bo\'limi') {
                deptComment = app.tt_comment || '';
              } else if (it.name === 'Ta\'lim sifati bo\'limi') {
                deptComment = app.ts_comment || '';
              } else if (it.name === 'O\'quv bo\'limi') {
                // For O'quv bo'limi, only use ov_comment if this department has actually responded
                // This prevents showing Xalqaro's old comment for O'quv
                if (it.st !== 0 && it.st !== undefined) {
                  deptComment = app.ov_comment || '';
                }
              } else if (it.name === 'Ma\'naviyat bo\'limi') {
                deptComment = app.mn_comment || '';

              } else if (it.name === 'Ilmiy bo\'lim') {
                // Ilmiy bo'lim uses ts_comment field (as per backend logic)
                deptComment = app.ts_comment || '';
              } else if (it.name === 'Xalqaro hamkorlik bo\'limi') {
                // Xalqaro hamkorlik uses tt_comment field (new data only)
                // Don't show old ov_comment data to avoid confusion with O'quv bo'limi
                deptComment = app.tt_comment || '';
              }

              return (
                <Box
                  key={idx}
                  p={2}
                  bg={bgColor}
                  borderLeft="3px solid"
                  borderColor={borderColor}
                  borderRadius={4}
                  display="grid"
                  gridTemplateColumns={deptComment ? "1fr auto 1fr" : "1fr auto"}
                  alignItems="center"
                  gap={3}
                >
                  <Text fontSize="sm" fontWeight={600} color="gray.700">
                    {it.name}
                  </Text>
                  <Text fontSize="sm" fontWeight={700} color={textColor} textAlign="center">
                    {status}
                  </Text>
                  {deptComment && (
                    <Text fontSize="sm" color={isRejected ? "red.700" : "green.700"} fontStyle="italic" textAlign="right">
                      {deptComment}
                    </Text>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      );
    } catch {
      return null;
    }
  };

  if (!param) return <Card style={{ padding: 20 }}>Topilmadi</Card>;

  return (
    <Layout>
      <Box p={4}>
        <Button onClick={() => navigate(-1)} mb={3} color="white" bg={PRIMARY} _hover={{ bg: PRIMARY }}>
          <ArrowBackIcon />
          <span style={{ marginLeft: 5 }}>Orqaga</span>
        </Button>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Heading size="sm">{param.no} — {param.parameter}</Heading>
          {fromWelcome || (['1.2', '1.5', '2.1', '2.2'].includes(String(param.no))) ? null : (
            <Button size="sm" onClick={() => setShowForm(v => !v)} color="white" bg={PRIMARY} _hover={{ bg: PRIMARY }}>
              {showForm ? 'Yopish' : '+ Yangi ariza'}
            </Button>
          )}
        </Box>

        <Card style={{ padding: 16, marginBottom: 12 }}>
          <Text fontWeight={600} mb={2}>Mexanizm</Text>
          <Text whiteSpace="pre-wrap">
            {(() => {
              // For KPI 1.1, replace hardcoded 25% with actual required_percentage
              if (param?.no === '1.1' && paramFromApi?.required_percentage) {
                const mechanismText = param.mechanism || '';
                // Replace "25%" with actual percentage
                return mechanismText.replace(/25%/g, `${paramFromApi.required_percentage}%`);
              }
              return param.mechanism;
            })()}
          </Text>
          <Text mt={3}><b>Maks. ball:</b> {param.maxBall}</Text>
          {param?.no?.startsWith('4.') && paramFromApi?.max_applications_count && paramFromApi.max_applications_count > 1 && (
            <Text mt={2} color="blue.600" fontSize="sm">
              {/* <b>{(parseFloat(param.maxBall) / paramFromApi.max_applications_count).toFixed(2)} ball</b> (maksimal {paramFromApi.max_applications_count} ta ariza) */}
            </Text>
          )}

          {/* For KPI 1.1, show required video lessons */}
          {is11 && (() => {
            // Extract total lessons from requirement
            const kpi11Requirement = requirementApps.find(app =>
              app.parameter?.code === '1.1'
            );

            if (kpi11Requirement) {
              try {
                const workText = kpi11Requirement.work || '';

                // Get required percentage from API parameter (default to 25 if not available)
                const requiredPercentage = paramFromApi?.required_percentage || 25;
                const percentageDecimal = requiredPercentage / 100;

                // Try to extract totalLessons from JSON
                const jsonMatch = workText.match(/\{"totalLessons":\s*"?(\d+)"?\}/);
                if (jsonMatch) {
                  const totalLessons = parseInt(jsonMatch[1]);
                  const requiredVideoLessons = Math.floor(totalLessons * percentageDecimal);

                  return (
                    <Text mt={2} color="blue.600" fontSize="sm">
                      💡 Maksimal ball olish uchun <b>{requiredVideoLessons} ta video dars</b> kerak (umumiy {totalLessons} darsning {requiredPercentage}%)
                    </Text>
                  );
                }

                // Try to extract from text pattern "Darslar soni: 200"
                const textMatch = workText.match(/Darslar soni:\s*(\d+)/i);
                if (textMatch) {
                  const totalLessons = parseInt(textMatch[1]);
                  const requiredVideoLessons = Math.floor(totalLessons * percentageDecimal);

                  return (
                    <Text mt={2} color="blue.600" fontSize="sm">
                      💡 Maksimal ball olish uchun <b>{requiredVideoLessons} ta video dars</b> kerak (umumiy {totalLessons} darsning {requiredPercentage}%)
                    </Text>
                  );
                }
              } catch (e) {
                console.error('Error parsing requirement:', e);
              }
            }

            return null;
          })()}
        </Card>

        {showForm ? (
          <Card ref={formRef} style={{ padding: 16, marginBottom: 12 }}>
            <Text fontWeight={600} mb={2}>{param.no} — {param.parameter}</Text>

            {is11 ? (
              <>
                <FormControl mb={3} isInvalid={error?.name11}>
                  <FormLabel>Nomi</FormLabel>
                  <Input value={name11} onChange={e => setName11(e.target.value)} />
                  {error?.name11 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3} isInvalid={error?.authorsCount11}>
                  <FormLabel>Mualliflar soni</FormLabel>
                  <Input type="number" value={authorsCount11} onChange={e => setAuthorsCount11(e.target.value)} />
                  {error?.authorsCount11 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                {/* Auto-filled from department requirement (read-only) - HIDDEN but value is kept for calculation */}
                {totalLessonsRequirement11 && (
                  <FormControl mb={3} display="none">
                    <FormLabel>Umumiy darslar soni (talabdan)</FormLabel>
                    <Input type="number" value={totalLessonsRequirement11} readOnly />
                    <FormHelperText color="gray.600">Bu qiymat bo'lim tomonidan belgilangan talabdan olinadi</FormHelperText>
                  </FormControl>
                )}
                <FormControl mb={3} isInvalid={error?.approvedDate11}>
                  <FormLabel>Tasdiqlangan sana</FormLabel>
                  <Input type="date" value={approvedDate11} onChange={e => setApprovedDate11(e.target.value)} />
                  {error?.approvedDate11 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3} isInvalid={error?.platformName11}>
                  <FormLabel>Platforma nomi</FormLabel>
                  <Input value={platformName11} onChange={e => setPlatformName11(e.target.value)} />
                  {error?.platformName11 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3} isInvalid={error?.internetAddress11}>
                  <FormLabel>Internet manzili</FormLabel>
                  <Input
                    value={internetAddress11}
                    onChange={e => setInternetAddress11(e.target.value)}
                    placeholder="https://example.com"
                  />
                  {error?.internetAddress11 && <FormHelperText color="red">{error?.internetAddress11Message || "Majburiy maydon"}</FormHelperText>}
                  {/* <FormHelperText color="blue.600" mt={2}>
                  💡 Har bir ariza 1 ta video darsga teng hisoblanadi. Maksimal ball olish uchun umumiy darslar sonining 25% i kerak.
                </FormHelperText> */}
                </FormControl>
                {/* Display total lessons count from department requirements */}
                {requirementApps.length > 0 && (() => {
                  const kpi11RequirementApps = requirementApps.filter(app =>
                    app.parameter?.code === '1.1' &&
                    app.work?.includes('totalLessons')
                  );

                  if (kpi11RequirementApps.length > 0) {
                    try {
                      const workText = kpi11RequirementApps[0].work || '';
                      const jsonMatch = workText.match(/\{"totalLessons":\s*\d+\}/);
                      if (jsonMatch) {
                        const requirementData = JSON.parse(jsonMatch[0]);
                        const totalLessons = requirementData.totalLessons;
                        return (
                          <FormControl mb={3} display="none">
                            <FormLabel>Umumiy darslar soni (bo'lim tomonidan belgilangan)</FormLabel>
                            <Input
                              value={totalLessons}
                              isReadOnly
                              bg="gray.100"
                              color="gray.700"
                            />
                            <FormHelperText color="blue.600">
                              Bu maydon bo'lim tomonidan belgilangan
                            </FormHelperText>
                          </FormControl>
                        );
                      }
                    } catch (e) {
                      console.error('Error parsing requirement data:', e);
                    }
                  }
                  return null;
                })()}

              </>
            ) : is12 ? (
              <>
                <FormControl mb={3} isInvalid={error?.educationDirection12}>
                  <FormLabel>Ta'lim yo'nalishi, mutaxassislik</FormLabel>
                  <Input value={educationDirection12} onChange={e => setEducationDirection12(e.target.value)} />
                  {error?.educationDirection12 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Guruhlar</FormLabel>
                  <Input value={groups12} onChange={e => setGroups12(e.target.value)} />
                </FormControl>
                <FormControl mb={3} isInvalid={error?.totalStudents12}>
                  <FormLabel>Dars o'tiladigan jami talaba soni</FormLabel>
                  <Input type="number" value={totalStudents12} onChange={e => setTotalStudents12(e.target.value)} />
                  {error?.totalStudents12 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3} isInvalid={error?.monitoringStudents12}>
                  <FormLabel>Monitoringda qatashgan talaba soni</FormLabel>
                  <Input type="number" value={monitoringStudents12} onChange={e => setMonitoringStudents12(e.target.value)} />
                  {error?.monitoringStudents12 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3} isInvalid={error?.averageScore12}>
                  <FormLabel>O'rtacha o'zlashtirish ko'rsatkichi (10 ballik tizimda)</FormLabel>
                  <Input type="number" step="0.1" value={averageScore12} onChange={e => setAverageScore12(e.target.value)} />
                  {error?.averageScore12 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
              </>
            ) : is13 ? (
              <>
                <FormControl mb={3} isInvalid={error?.name13}>
                  <FormLabel>Nomi va turi</FormLabel>
                  <Input value={name13} onChange={e => setName13(e.target.value)} />
                  {error?.name13 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3} isInvalid={error?.studentInfo13}>
                  <FormLabel>Talabaning FISh, kursi, guruhi</FormLabel>
                  <Input value={studentInfo13} onChange={e => setStudentInfo13(e.target.value)} />
                  {error?.studentInfo13 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Joyi, sanasi</FormLabel>
                  <Input value={location13} onChange={e => setLocation13(e.target.value)} />
                </FormControl>
                <FormControl mb={3} isInvalid={error?.achievement13}>
                  <FormLabel>Egallagan o'rni</FormLabel>
                  <Select value={achievement13} onChange={e => setAchievement13(e.target.value)}>
                    <option value="">Tanlang</option>
                    <option value="1">1-o'rin</option>
                    <option value="2">2-o'rin</option>
                    <option value="3">3-o'rin</option>
                  </Select>
                  {error?.achievement13 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Diplom seriyasi va raqami</FormLabel>
                  <Input value={diplomaSerial13} onChange={e => setDiplomaSerial13(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Rahbarlik hujjati fayli (PDF)</FormLabel>
                  <Input type="file"
                    accept=".pdf"
                    onChange={e => setGuidanceFile13(e.target.files?.[0] || null)} />
                  {guidanceFile13 && (
                    <FormHelperText color="green.600">
                      Tanlangan fayl: {guidanceFile13.name}
                    </FormHelperText>
                  )}
                </FormControl>
              </>
            ) : is14 ? (
              <>
                <FormControl mb={3} isInvalid={error?.name14}>
                  <FormLabel>Nomi va turi</FormLabel>
                  <Input value={name14} onChange={e => setName14(e.target.value)} />
                  {error?.name14 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3} isInvalid={error?.studentInfo14}>
                  <FormLabel>Talabaning FISh, kursi, guruhi</FormLabel>
                  <Input value={studentInfo14} onChange={e => setStudentInfo14(e.target.value)} />
                  {error?.studentInfo14 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Joyi, sanasi</FormLabel>
                  <Input value={location14} onChange={e => setLocation14(e.target.value)} />
                </FormControl>
                <FormControl mb={3} isInvalid={error?.achievement14}>
                  <FormLabel>Egallagan o'rni</FormLabel>
                  <Select value={achievement14} onChange={e => setAchievement14(e.target.value)}>
                    <option value="">Tanlang</option>
                    <option value="1">1-o'rin</option>
                    <option value="2">2-o'rin</option>
                    <option value="3">3-o'rin</option>
                  </Select>
                  {error?.achievement14 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Diplom seriyasi va raqami</FormLabel>
                  <Input value={diplomaSerial14} onChange={e => setDiplomaSerial14(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Rahbarlik hujjati fayli (PDF)</FormLabel>
                  <Input type="file"
                    accept=".pdf"
                    onChange={e => setGuidanceFile14(e.target.files?.[0] || null)} />
                  {guidanceFile14 && (
                    <FormHelperText color="green.600">
                      Tanlangan fayl: {guidanceFile14.name}
                    </FormHelperText>
                  )}
                </FormControl>
              </>
            ) : is16 ? (
              <>
                <FormControl mb={3} isInvalid={error?.name16}>
                  <FormLabel>Nomi</FormLabel>
                  <Input value={name16} onChange={e => setName16(e.target.value)} />
                  {error?.name16 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3} isInvalid={error?.publishTime16}>
                  <FormLabel>Nashr etilgan vaqti</FormLabel>
                  <Input type="date" value={publishTime16} onChange={e => setPublishTime16(e.target.value)} />
                  {error?.publishTime16 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3} isInvalid={error?.authorsCount16}>
                  <FormLabel>Mualliflar soni</FormLabel>
                  <Input type="number" value={authorsCount16} onChange={e => setAuthorsCount16(e.target.value)} />
                  {error?.authorsCount16 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Xorijlik mualliflar soni</FormLabel>
                  <Input type="number" value={foreignAuthors16} onChange={e => setForeignAuthors16(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Tili</FormLabel>
                  <Box display="flex" gap={2} alignItems="flex-start">
                    <Select
                      value={language16}
                      onChange={e => {
                        setLanguage16(e.target.value);
                        if (e.target.value !== 'Boshqa') {
                          setCustomLanguage16('');
                        }
                      }}
                      placeholder="Tilni tanlang"
                      flex={language16 === 'Boshqa' ? '1' : 'auto'}
                      width={language16 === 'Boshqa' ? 'auto' : '100%'}
                    >
                      {LANGUAGE_OPTIONS.map(lang => (
                        <option key={lang} value={lang}>{lang}</option>
                      ))}
                      <option value="Boshqa">Boshqa</option>
                    </Select>
                    {language16 === 'Boshqa' && (
                      <Input
                        value={customLanguage16}
                        onChange={e => setCustomLanguage16(e.target.value)}
                        placeholder="Til nomini kiriting"
                        flex="1"
                      />
                    )}
                  </Box>
                  {error?.customLanguage16 && language16 === 'Boshqa' && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Nashr parametlari (nashriyot, sahifasi)</FormLabel>
                  <Input value={publishParams16} onChange={e => setPublishParams16(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Yo'nalishi</FormLabel>
                  <Input value={direction16} onChange={e => setDirection16(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Ruxsatnoma fayli (PDF)</FormLabel>
                  <Input type="file"
                    accept=".pdf"
                    onChange={e => setPermissionFile16(e.target.files?.[0] || null)} />
                  {permissionFile16 && (
                    <FormHelperText color="green.600">
                      Tanlangan fayl: {permissionFile16.name}
                    </FormHelperText>
                  )}
                </FormControl>
              </>
            ) : is17 ? (
              <>
                <FormControl mb={3} isInvalid={error?.name17}>
                  <FormLabel>Nomi</FormLabel>
                  <Input value={name17} onChange={e => setName17(e.target.value)} />
                  {error?.name17 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3} isInvalid={error?.publishTime17}>
                  <FormLabel>Nashr etilgan vaqti</FormLabel>
                  <Input type="date" value={publishTime17} onChange={e => setPublishTime17(e.target.value)} />
                  {error?.publishTime17 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3} isInvalid={error?.authorsCount17}>
                  <FormLabel>Mualliflar soni</FormLabel>
                  <Input type="number" value={authorsCount17} onChange={e => setAuthorsCount17(e.target.value)} />
                  {error?.authorsCount17 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Xorijlik mualliflar soni</FormLabel>
                  <Input type="number" value={foreignAuthors17} onChange={e => setForeignAuthors17(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Tili</FormLabel>
                  <Box display="flex" gap={2} alignItems="flex-start">
                    <Select
                      value={language17}
                      onChange={e => {
                        setLanguage17(e.target.value);
                        if (e.target.value !== 'Boshqa') {
                          setCustomLanguage17('');
                        }
                      }}
                      placeholder="Tilni tanlang"
                      flex={language17 === 'Boshqa' ? '1' : 'auto'}
                      width={language17 === 'Boshqa' ? 'auto' : '100%'}
                    >
                      {LANGUAGE_OPTIONS.map(lang => (
                        <option key={lang} value={lang}>{lang}</option>
                      ))}
                      <option value="Boshqa">Boshqa</option>
                    </Select>
                    {language17 === 'Boshqa' && (
                      <Input
                        value={customLanguage17}
                        onChange={e => setCustomLanguage17(e.target.value)}
                        placeholder="Til nomini kiriting"
                        flex="1"
                      />
                    )}
                  </Box>
                  {error?.customLanguage17 && language17 === 'Boshqa' && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Nashr parametlari (nashriyot, sahifasi)</FormLabel>
                  <Input value={publishParams17} onChange={e => setPublishParams17(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Turi</FormLabel>
                  <Select
                    placeholder="Tanlang"
                    value={direction17}
                    onChange={e => setDirection17(e.target.value)}
                  >
                    <option value="O'quv qo'llanma">O'quv qo'llanma</option>
                    <option value="Monografiya">Monografiya</option>
                  </Select>
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Ruxsatnoma fayli (PDF)</FormLabel>
                  <Input type="file"
                    accept=".pdf"
                    onChange={e => setPermissionFile17(e.target.files?.[0] || null)} />
                  {permissionFile17 && (
                    <FormHelperText color="green.600">
                      Tanlangan fayl: {permissionFile17.name}
                    </FormHelperText>
                  )}
                </FormControl>
              </>
            ) : is18 ? (
              <>
                <FormControl mb={3} isInvalid={error?.certType18}>
                  <FormLabel>Sertifikat turi</FormLabel>
                  <Input value={certType18} onChange={e => setCertType18(e.target.value)} />
                  {error?.certType18 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3} isInvalid={error?.level18}>
                  <FormLabel>Daraja</FormLabel>
                  <Input value={level18} onChange={e => setLevel18(e.target.value)} />
                  {error?.level18 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3} isInvalid={error?.date18}>
                  <FormLabel>Sana</FormLabel>
                  <Input type="date" value={date18} onChange={e => setDate18(e.target.value)} />
                  {error?.date18 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Sertifikat fayli (PDF)</FormLabel>
                  <Input type="file"
                    accept=".pdf"
                    onChange={e => setCertFile18(e.target.files?.[0] || null)} />
                  {certFile18 && (
                    <FormHelperText color="green.600">
                      Tanlangan fayl: {certFile18.name}
                    </FormHelperText>
                  )}
                </FormControl>
              </>
            ) : is19 ? (
              <>
                <FormControl mb={3} isInvalid={error?.type19}>
                  <FormLabel>Tur</FormLabel>
                  <Input value={type19} onChange={e => setType19(e.target.value)} />
                  {error?.type19 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3} isInvalid={error?.location19}>
                  <FormLabel>Joyi</FormLabel>
                  <Input value={location19} onChange={e => setLocation19(e.target.value)} />
                  {error?.location19 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3} isInvalid={error?.startDate19}>
                  <FormLabel>Boshlanish sanasi</FormLabel>
                  <Input type="date" value={startDate19} onChange={e => setStartDate19(e.target.value)} />
                  {error?.startDate19 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3} isInvalid={error?.endDate19}>
                  <FormLabel>Tugash sanasi</FormLabel>
                  <Input type="date" value={endDate19} onChange={e => setEndDate19(e.target.value)} />
                  {error?.endDate19 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Yo'nalishi</FormLabel>
                  <Input value={direction19} onChange={e => setDirection19(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Maqsadi</FormLabel>
                  <Input value={purpose19} onChange={e => setPurpose19(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Hujjat fayli (PDF)</FormLabel>
                  <Input type="file"
                    accept=".pdf"
                    onChange={e => setDocFile19(e.target.files?.[0] || null)} />
                  {docFile19 && (
                    <FormHelperText color="green.600">
                      Tanlangan fayl: {docFile19.name}
                    </FormHelperText>
                  )}
                </FormControl>
              </>
            ) : is21 ? (
              <>
                <FormControl mb={3} isInvalid={error?.degreeType21}>
                  <FormLabel>Daraja turi</FormLabel>
                  <Input value={degreeType21} onChange={e => setDegreeType21(e.target.value)} />
                  {error?.degreeType21 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3} isInvalid={error?.diplomaSerial21}>
                  <FormLabel>Diplom seriyasi</FormLabel>
                  <Input value={diplomaSerial21} onChange={e => setDiplomaSerial21(e.target.value)} />
                  {error?.diplomaSerial21 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Diplom raqami</FormLabel>
                  <Input value={diplomaNumber21} onChange={e => setDiplomaNumber21(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Diplom sanasi</FormLabel>
                  <Input type="date" value={diplomaDate21} onChange={e => setDiplomaDate21(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Mutaxassislik</FormLabel>
                  <Input value={specialty21} onChange={e => setSpecialty21(e.target.value)} />
                </FormControl>
              </>
            ) : is22 ? (
              <>
                <FormControl mb={3} isInvalid={error?.specialtyCode22}>
                  <FormLabel>Mutaxassislik kodi</FormLabel>
                  <Input value={specialtyCode22} onChange={e => setSpecialtyCode22(e.target.value)} />
                  {error?.specialtyCode22 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3} isInvalid={error?.specialtyName22}>
                  <FormLabel>Mutaxassislik nomi</FormLabel>
                  <Input value={specialtyName22} onChange={e => setSpecialtyName22(e.target.value)} />
                  {error?.specialtyName22 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Unvon berilgan sana</FormLabel>
                  <Input type="date" value={titleDate22} onChange={e => setTitleDate22(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Unvon seriyasi</FormLabel>
                  <Input value={titleSerial22} onChange={e => setTitleSerial22(e.target.value)} />
                </FormControl>
              </>
            ) : is23 ? (
              <>
                <FormControl mb={3}>
                  <FormLabel>Shogirdining FISh</FormLabel>
                  <Input value={studentName23} onChange={e => setStudentName23(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Shogirdining lavozimi</FormLabel>
                  <Select value={studentPosition23} onChange={e => setStudentPosition23(e.target.value)}>
                    <option value="">Tanlang</option>
                    <option value="Fan doktori">Fan doktori (2 ball)</option>
                    <option value="Falsafa doktori">Falsafa doktori (1 ball)</option>
                  </Select>
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Ish bajarilgan tashkilot</FormLabel>
                  <Input value={organization23} onChange={e => setOrganization23(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Himoya sanasi</FormLabel>
                  <Input type="date" value={defenseDate23} onChange={e => setDefenseDate23(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Ixtisosligi shifri va nomi</FormLabel>
                  <Input value={specialtyCode23} taxassislik kodi onChange={e => setSpecialtyCode23(e.target.value)} />
                </FormControl>
              </>
            ) : is24 ? (
              <>
                <FormControl mb={3} isInvalid={error?.journalName24}>
                  <FormLabel>Jurnal nomi</FormLabel>
                  <Input value={journalName24} onChange={e => setJournalName24(e.target.value)} />
                  {error?.journalName24 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Ma'lumot bazasi</FormLabel>
                  <Select
                    value={database24}
                    onChange={e => setDatabase24(e.target.value)}
                    placeholder="Tanlang"
                  >
                    <option value="Scopus">Scopus</option>
                    <option value="Web of Science">Web of Science</option>
                  </Select>
                </FormControl>
                <FormControl mb={3} isInvalid={error?.articleName24}>
                  <FormLabel>Maqola nomi</FormLabel>
                  <Input value={articleName24} onChange={e => setArticleName24(e.target.value)} />
                  {error?.articleName24 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Nashr sanasi</FormLabel>
                  <Input type="date" value={publishDate24} onChange={e => setPublishDate24(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Mamlakat</FormLabel>
                  <SearchableCountrySelect
                    value={country24}
                    onChange={setCountry24}
                    countries={countries}
                    loading={loadingCountries}
                    placeholder="Mamlakatni kiriting yoki tanlang"
                    listId="countries-list-24"
                  />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Mualliflar soni</FormLabel>
                  <Input type="number" value={authorsCount24} onChange={e => setAuthorsCount24(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Xorijlik mualliflar soni</FormLabel>
                  <Input type="number" value={foreignAuthors24} onChange={e => setForeignAuthors24(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Maqola tili</FormLabel>
                  <Box display="flex" gap={2} alignItems="flex-start">
                    <Select
                      value={articleLang24}
                      onChange={e => {
                        setArticleLang24(e.target.value);
                        if (e.target.value !== 'Boshqa') {
                          setCustomArticleLang24('');
                        }
                      }}
                      placeholder="Tilni tanlang"
                      flex={articleLang24 === 'Boshqa' ? '1' : 'auto'}
                      width={articleLang24 === 'Boshqa' ? 'auto' : '100%'}
                    >
                      {LANGUAGE_OPTIONS.map(lang => (
                        <option key={lang} value={lang}>{lang}</option>
                      ))}
                      <option value="Boshqa">Boshqa</option>
                    </Select>
                    {articleLang24 === 'Boshqa' && (
                      <Input
                        value={customArticleLang24}
                        onChange={e => setCustomArticleLang24(e.target.value)}
                        placeholder="Til nomini kiriting"
                        flex="1"
                      />
                    )}
                  </Box>
                  {error?.customArticleLang24 && articleLang24 === 'Boshqa' && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Nashr parametrlari</FormLabel>
                  <Input value={publishParams24} onChange={e => setPublishParams24(e.target.value)} />
                </FormControl>
                <FormControl mb={3} isInvalid={error?.profileUrl24}>
                  <FormLabel>Profil manzili</FormLabel>
                  <Input
                    value={profileUrl24}
                    onChange={e => setProfileUrl24(e.target.value)}
                    placeholder="https://example.com"
                  />
                  {error?.profileUrl24 && (
                    <FormHelperText color="red">
                      {error?.profileUrl24Message || "Havola yaroqsiz"}
                    </FormHelperText>
                  )}
                </FormControl>
              </>
            ) : is25 ? (
              <>
                <FormControl mb={3}>
                  <FormLabel>Jurnal nomi</FormLabel>
                  <Input value={journalName25} onChange={e => setJournalName25(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>OAK ro'yxati</FormLabel>
                  <Input value={oakRegistry25} onChange={e => setOakRegistry25(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Maqola nomi</FormLabel>
                  <Input value={articleName25} onChange={e => setArticleName25(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Nashr sanasi</FormLabel>
                  <Input type="date" value={publishDate25} onChange={e => setPublishDate25(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Mamlakat</FormLabel>
                  <SearchableCountrySelect
                    value={country25}
                    onChange={setCountry25}
                    countries={countries}
                    loading={loadingCountries}
                    placeholder="Mamlakatni kiriting yoki tanlang"
                    listId="countries-list-25"
                  />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Mualliflar soni</FormLabel>
                  <Input type="number" value={authorsCount25} onChange={e => setAuthorsCount25(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Xorijlik mualliflar soni</FormLabel>
                  <Input type="number" value={foreignAuthors25} onChange={e => setForeignAuthors25(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Maqola tili</FormLabel>
                  <Box display="flex" gap={2} alignItems="flex-start">
                    <Select
                      value={articleLang25}
                      onChange={e => {
                        setArticleLang25(e.target.value);
                        if (e.target.value !== 'Boshqa') {
                          setCustomArticleLang25('');
                        }
                      }}
                      placeholder="Tilni tanlang"
                      flex={articleLang25 === 'Boshqa' ? '1' : 'auto'}
                      width={articleLang25 === 'Boshqa' ? 'auto' : '100%'}
                    >
                      {LANGUAGE_OPTIONS.map(lang => (
                        <option key={lang} value={lang}>{lang}</option>
                      ))}
                      <option value="Boshqa">Boshqa</option>
                    </Select>
                    {articleLang25 === 'Boshqa' && (
                      <Input
                        value={customArticleLang25}
                        onChange={e => setCustomArticleLang25(e.target.value)}
                        placeholder="Til nomini kiriting"
                        flex="1"
                      />
                    )}
                  </Box>
                  {error?.customArticleLang25 && articleLang25 === 'Boshqa' && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Nashr parametrlari</FormLabel>
                  <Input value={publishParams25} onChange={e => setPublishParams25(e.target.value)} />
                </FormControl>
                <FormControl mb={3} isInvalid={error?.articleUrl25}>
                  <FormLabel>Maqola manzili</FormLabel>
                  <Input
                    value={articleUrl25}
                    onChange={e => setArticleUrl25(e.target.value)}
                    placeholder="https://example.com/article"
                  />
                  {error?.articleUrl25 && (
                    <FormHelperText color="red">
                      {error?.articleUrl25Message || "Majburiy maydon"}
                    </FormHelperText>
                  )}
                </FormControl>
              </>
            ) : is26 ? (
              <>
                <FormControl mb={3}>
                  <FormLabel>Ma'lumot bazasi</FormLabel>
                  <Select value={database26} onChange={e => setDatabase26(e.target.value)}>
                    <option value="">Tanlang</option>
                    <option value="Scopus">Scopus</option>
                    <option value="Web of Science">Web of Science</option>
                    <option value="RG">RG</option>
                    <option value="GS">GS</option>
                  </Select>
                </FormControl>
                <FormControl mb={3} isInvalid={error?.profileUrl26}>
                  <FormLabel>Profil manzili</FormLabel>
                  <Input
                    value={profileUrl26}
                    onChange={e => setProfileUrl26(e.target.value)}
                    placeholder="https://scholar.google.com/..."
                  />
                  {error?.profileUrl26 && (
                    <FormHelperText color="red">
                      {error?.profileUrl26Message || "Havola yaroqsiz"}
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Maqolalar soni</FormLabel>
                  <Input type="number" value={articleCount26} onChange={e => setArticleCount26(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>H-indeks</FormLabel>
                  <Input type="number" value={hIndex26} onChange={e => setHIndex26(e.target.value)} />
                </FormControl>
              </>
            ) : is27 ? (
              <>
                <FormControl mb={3}>
                  <FormLabel>Jurnal nomi</FormLabel>
                  <Input value={journalName27} onChange={e => setJournalName27(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>OAK ro'yxati</FormLabel>
                  <Input value={oakRegistry27} onChange={e => setOakRegistry27(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Maqola nomi</FormLabel>
                  <Input value={articleName27} onChange={e => setArticleName27(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Nashr sanasi</FormLabel>
                  <Input type="date" value={publishDate27} onChange={e => setPublishDate27(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Mualliflar soni</FormLabel>
                  <Input type="number" value={authorsCount27} onChange={e => setAuthorsCount27(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Xorijlik mualliflar soni</FormLabel>
                  <Input type="number" value={foreignAuthors27} onChange={e => setForeignAuthors27(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Maqola tili</FormLabel>
                  <Box display="flex" gap={2} alignItems="flex-start">
                    <Select
                      value={articleLang27}
                      onChange={e => {
                        setArticleLang27(e.target.value);
                        if (e.target.value !== 'Boshqa') {
                          setCustomArticleLang27('');
                        }
                      }}
                      placeholder="Tilni tanlang"
                      flex={articleLang27 === 'Boshqa' ? '1' : 'auto'}
                      width={articleLang27 === 'Boshqa' ? 'auto' : '100%'}
                    >
                      {LANGUAGE_OPTIONS.map(lang => (
                        <option key={lang} value={lang}>{lang}</option>
                      ))}
                      <option value="Boshqa">Boshqa</option>
                    </Select>
                    {articleLang27 === 'Boshqa' && (
                      <Input
                        value={customArticleLang27}
                        onChange={e => setCustomArticleLang27(e.target.value)}
                        placeholder="Til nomini kiriting"
                        flex="1"
                      />
                    )}
                  </Box>
                  {error?.customArticleLang27 && articleLang27 === 'Boshqa' && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Nashr parametrlari</FormLabel>
                  <Input value={publishParams27} onChange={e => setPublishParams27(e.target.value)} />
                </FormControl>
                <FormControl mb={3} isInvalid={error?.articleUrl27}>
                  <FormLabel>Maqola manzili</FormLabel>
                  <Input
                    value={articleUrl27}
                    onChange={e => setArticleUrl27(e.target.value)}
                    placeholder="https://example.com/article"
                  />
                  {error?.articleUrl27 && (
                    <FormHelperText color="red">
                      {error?.articleUrl27Message || "Majburiy maydon"}
                    </FormHelperText>
                  )}
                </FormControl>
              </>
            ) : is28 ? (
              <>
                <FormControl mb={3}>
                  <FormLabel>Patent nomi</FormLabel>
                  <Input value={patentName28} onChange={e => setPatentName28(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Patent turi</FormLabel>
                  <Input value={patentType28} onChange={e => setPatentType28(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Patent sanasi</FormLabel>
                  <Input type="date" value={patentDate28} onChange={e => setPatentDate28(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Ro'yxatga olish raqami</FormLabel>
                  <Input value={registrationNumber28} onChange={e => setRegistrationNumber28(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Egalar soni</FormLabel>
                  <Input type="number" value={ownersCount28} onChange={e => setOwnersCount28(e.target.value)} />
                </FormControl>
              </>
            ) : is29 ? (
              <>
                <FormControl mb={3}>
                  <FormLabel>Loyiha nomi</FormLabel>
                  <Input value={projectName29} onChange={e => setProjectName29(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Shartnoma sanasi</FormLabel>
                  <Input type="date" value={contractDate29} onChange={e => setContractDate29(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Umumiy summa</FormLabel>
                  <Input type="number" value={totalAmount29} onChange={e => setTotalAmount29(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Ishtirokchilar soni</FormLabel>
                  <Input type="number" value={participantsCount29} onChange={e => setParticipantsCount29(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Olingan summa</FormLabel>
                  <Input type="number" value={receivedAmount29} onChange={e => setReceivedAmount29(e.target.value)} />
                </FormControl>
              </>
            ) : is210 ? (
              <>
                <FormControl mb={3}>
                  <FormLabel>Grant nomi</FormLabel>
                  <Input value={grantName210} onChange={e => setGrantName210(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Grant turi</FormLabel>
                  <Select value={grantType210} onChange={e => setGrantType210(e.target.value)}>
                    <option value="">Tanlang</option>
                    <option value="Universitetdagi">Universitetdagi</option>
                    <option value="Boshqa muassasada">Boshqa muassasada</option>
                  </Select>
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Loyiha raqami</FormLabel>
                  <Input value={projectNumber210} onChange={e => setProjectNumber210(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Shartnoma raqami</FormLabel>
                  <Input value={contractNumber210} onChange={e => setContractNumber210(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Loyiha davomiyligi</FormLabel>
                  <Input value={projectDuration210} onChange={e => setProjectDuration210(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Umumiy summa</FormLabel>
                  <Input type="number" value={totalSum210} onChange={e => setTotalSum210(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Ishtirokchilar soni</FormLabel>
                  <Input type="number" value={participantsCount210} onChange={e => setParticipantsCount210(e.target.value)} />
                </FormControl>
              </>
            ) : is211 ? (
              <>
                <FormControl mb={3}>
                  <FormLabel>Kengash nomi</FormLabel>
                  <Input value={councilName211} onChange={e => setCouncilName211(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Tashkilot nomi</FormLabel>
                  <Input value={organizationName211} onChange={e => setOrganizationName211(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Mutaxassislik</FormLabel>
                  <Input value={specialty211} onChange={e => setSpecialty211(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Lavozim</FormLabel>
                  <Select value={position211} onChange={e => setPosition211(e.target.value)}>
                    <option value="">Tanlang</option>
                    <option value="IK va ITK a'zoligi">IK va ITK a'zoligi</option>
                    <option value="IS a'zolik">IS a'zolik</option>
                  </Select>
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>A'zolik sanasi</FormLabel>
                  <Input type="date" value={membershipDate211} onChange={e => setMembershipDate211(e.target.value)} />
                </FormControl>
              </>
            ) : is31 ? (
              <>
                <FormControl mb={3}>
                  <FormLabel>Xorijiy mamlakat</FormLabel>
                  <SearchableCountrySelect
                    value={foreignCountry31}
                    onChange={setForeignCountry31}
                    countries={countries.filter(c => c.toLowerCase() !== 'uzbekistan' && c.toLowerCase() !== 'o\'zbekiston')}
                    loading={loadingCountries}
                    placeholder="Mamlakatni kiriting yoki tanlang"
                    listId="countries-list-31"
                  />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Xorijiy universitet</FormLabel>
                  <Input value={foreignUniversity31} onChange={e => setForeignUniversity31(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Daraja olgan sana</FormLabel>
                  <Input type="date" value={degreeDate31} onChange={e => setDegreeDate31(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Daraja darajasi</FormLabel>
                  <Input value={degreeLevel31} onChange={e => setDegreeLevel31(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Diplom seriyasi</FormLabel>
                  <Input value={diplomaSerial31} onChange={e => setDiplomaSerial31(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Mutaxassislik nomi</FormLabel>
                  <Input value={specialtyName31} onChange={e => setSpecialtyName31(e.target.value)} />
                </FormControl>
              </>
            ) : is32 ? (
              <>
                <FormControl mb={3}>
                  <FormLabel>Xorijiy mamlakat</FormLabel>
                  <SearchableCountrySelect
                    value={foreignCountry32}
                    onChange={setForeignCountry32}
                    countries={countries.filter(c => c.toLowerCase() !== 'uzbekistan' && c.toLowerCase() !== 'o\'zbekiston')}
                    loading={loadingCountries}
                    placeholder="Mamlakatni kiriting yoki tanlang"
                    listId="countries-list-32"
                  />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Xorijiy universitet</FormLabel>
                  <Input value={foreignUniversity32} onChange={e => setForeignUniversity32(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Mutaxassislik nomi</FormLabel>
                  <Input value={specialtyName32} onChange={e => setSpecialtyName32(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Ma'ruza turi</FormLabel>
                  <Input value={lectureType32} onChange={e => setLectureType32(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Davomiyligi</FormLabel>
                  <Input value={duration32} onChange={e => setDuration32(e.target.value)} />
                </FormControl>
              </>
            ) : is33 ? (
              <>
                <FormControl mb={3}>
                  <FormLabel>Treining turi</FormLabel>
                  <Input value={trainingType33} onChange={e => setTrainingType33(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Treining joyi</FormLabel>
                  <Input value={trainingLocation33} onChange={e => setTrainingLocation33(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Treining sanasi</FormLabel>
                  <Input type="date" value={trainingDate33} onChange={e => setTrainingDate33(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Treining yo'nalishi</FormLabel>
                  <Input value={trainingDirection33} onChange={e => setTrainingDirection33(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Treining maqsadi</FormLabel>
                  <Input value={trainingPurpose33} onChange={e => setTrainingPurpose33(e.target.value)} />
                </FormControl>
              </>
            ) : is34 ? (
              <>
                <FormControl mb={3}>
                  <FormLabel>Hamkorlik nomi</FormLabel>
                  <Input value={cooperationName34} onChange={e => setCooperationName34(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Hamkorlik joyi</FormLabel>
                  <Input value={cooperationLocation34} onChange={e => setCooperationLocation34(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Hamkorlik sanasi</FormLabel>
                  <Input type="date" value={cooperationDate34} onChange={e => setCooperationDate34(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Hamkorlik yo'nalishi</FormLabel>
                  <Input value={cooperationDirection34} onChange={e => setCooperationDirection34(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Hamkorlik maqsadi</FormLabel>
                  <Input value={cooperationPurpose34} onChange={e => setCooperationPurpose34(e.target.value)} />
                </FormControl>
              </>
            ) : is41 ? (
              <>
                <FormControl mb={3}>
                  <FormLabel>Tadbir nomi</FormLabel>
                  <Input value={eventName41} onChange={e => setEventName41(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Tadbir sanasi</FormLabel>
                  <Input type="date" value={eventDate41} onChange={e => setEventDate41(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Tadbir joyi</FormLabel>
                  <Input value={eventLocation41} onChange={e => setEventLocation41(e.target.value)} />
                </FormControl>
              </>
            ) : is42 ? (
              <>
                <FormControl mb={3}>
                  <FormLabel>To'garak nomi</FormLabel>
                  <Input value={clubName42} onChange={e => setClubName42(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>To'garak nizomi</FormLabel>
                  <Textarea value={clubRegulations42} onChange={e => setClubRegulations42(e.target.value)} />
                </FormControl>
              </>
            ) : is43 ? (
              <>
                <FormControl mb={3}>
                  <FormLabel>Faoliyat nomi</FormLabel>
                  <Input value={activityName43} onChange={e => setActivityName43(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Faoliyat sanasi</FormLabel>
                  <Input type="date" value={activityDate43} onChange={e => setActivityDate43(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Faoliyat joyi</FormLabel>
                  <Input value={activityLocation43} onChange={e => setActivityLocation43(e.target.value)} />
                </FormControl>
              </>
            ) : is44 ? (
              <>
                <FormControl mb={3}>
                  <FormLabel>Loyiha nomi</FormLabel>
                  <Input value={projectName44} onChange={e => setProjectName44(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Loyiha sanasi</FormLabel>
                  <Input type="date" value={projectDate44} onChange={e => setProjectDate44(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Loyiha joyi</FormLabel>
                  <Input value={projectLocation44} onChange={e => setProjectLocation44(e.target.value)} />
                </FormControl>
              </>
            ) : is45 ? (
              <>
                <FormControl mb={3}>
                  <FormLabel>Tanlov nomi</FormLabel>
                  <Input value={competitionName45} onChange={e => setCompetitionName45(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Tanlov turi</FormLabel>
                  <Input value={competitionType45} onChange={e => setCompetitionType45(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Talaba FISh</FormLabel>
                  <Input value={studentFish45} onChange={e => setStudentFish45(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Tanlov joyi</FormLabel>
                  <Input value={competitionLocation45} onChange={e => setCompetitionLocation45(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Tanlov sanasi</FormLabel>
                  <Input type="date" value={competitionDate45} onChange={e => setCompetitionDate45(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Egallagan o'rni</FormLabel>
                  <Input value={achievement45} onChange={e => setAchievement45(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Diplom seriyasi</FormLabel>
                  <Input value={diplomaSerial45} onChange={e => setDiplomaSerial45(e.target.value)} />
                </FormControl>
              </>
            ) : is46 ? (
              <>
                <FormControl mb={3}>
                  <FormLabel>OAV mavzusi</FormLabel>
                  <Input value={mediaSubject46} onChange={e => setMediaSubject46(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>OAV sanasi</FormLabel>
                  <Input type="date" value={mediaDate46} onChange={e => setMediaDate46(e.target.value)} />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>OAV nomi</FormLabel>
                  <Input value={mediaName46} onChange={e => setMediaName46(e.target.value)} />
                </FormControl>
              </>
            ) : is47 ? (
              <>
                <FormControl mb={3} isInvalid={error?.name47}>
                  <FormLabel>Nomi</FormLabel>
                  <Input value={name47} onChange={e => setName47(e.target.value)} />
                  {error?.name47 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3} isInvalid={error?.time47}>
                  <FormLabel>Vaqti</FormLabel>
                  <Input value={time47} onChange={e => setTime47(e.target.value)} />
                  {error?.time47 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
                <FormControl mb={3} isInvalid={error?.report47}>
                  <FormLabel>Hisobot</FormLabel>
                  <Textarea value={report47} onChange={e => setReport47(e.target.value)} />
                  {error?.report47 && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
                </FormControl>
              </>
            ) : (
              <FormControl mb={3} isInvalid={error?.desc}>
                <FormLabel>Bajarilgan ish haqida ma'lumot</FormLabel>
                <Textarea value={desc} onChange={e => setDesc(e.target.value)} />
                {error?.desc && <FormHelperText color="red">Majburiy maydon</FormHelperText>}
              </FormControl>
            )}

            <FormControl mb={3} isInvalid={error?.files}>
              <FormLabel>Asoslovchi fayllar (PDF) *</FormLabel>

              {/* Show existing files when editing */}
              {editId && existingFiles.length > 0 && (
                <Box mb={2} p={2} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                  {/* s */}
                  {existingFiles.map((file, idx) => {
                    const fileName = file.name || `Fayl ${idx + 1}`;
                    const filePath = file.file || '';
                    return (
                      <Box key={idx} display="flex" alignItems="center" gap={2} mb={1}>

                        {filePath && (
                          <a
                            href={`${FILE_URL}${filePath}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: '12px', color: '#3182ce', textDecoration: 'underline' }}
                          >
                            <Text fontSize="sm" color="blue.600">📄 {fileName}</Text>
                          </a>
                        )}
                      </Box>
                    );
                  })}
                  {/* <Text fontSize="xs" color="gray.600" mt={2}>
                    Yangi fayllar yuklasangiz, eski fayllar o'chiriladi
                  </Text> */}
                </Box>
              )}

              <Input type='file' multiple accept=".pdf" onChange={e => setFiles(Array.from(e.target.files || []))} />
              {error?.files && <FormHelperText color="red">{error?.filesMessage || "Majburiy maydon"}</FormHelperText>}
            </FormControl>
            {error?.common_e && (
              <FormControl>
                <FormHelperText className='error'>{error.common_e}</FormHelperText>
              </FormControl>
            )}
            <Box display="flex" gap={3} mt={4}>
              <Button onClick={submit} color="white" bg={PRIMARY} _hover={{ bg: PRIMARY }}>
                {editId ? 'Saqlash' : 'Tasdiqlash'}
              </Button>
              <Button variant="outline" onClick={() => { cancelEdit(); setShowForm(false); }}>
                Bekor qilish
              </Button>
            </Box>
          </Card>
        ) : null}

        {/* Display requirement applications from department */}
        {/* For KPI 2.1 and 2.2, don't show requirements card - they only have direct point assignment */}
        {requirementApps.length > 0 && !['2.1', '2.2'].includes(String(param?.no)) && (
          <Card style={{ padding: 16, marginBottom: 12, background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', border: '2px solid #3b82f6', borderRadius: '8px', boxShadow: '0 2px 8px rgba(59, 130, 246, 0.15)' }}>
            <Box mb={3}>
              <Text fontWeight={700} mb={1} fontSize="md" color="blue.900">Menga belgilangan talablar</Text>
              <Box height="2px" bg="blue.500" borderRadius="1px" width="60px"></Box>
            </Box>
            {requirementApps.map(app => (
              <Box
                key={app.id}
                p={3}
                mb={3}
                borderRadius={6}
                bg="white"
                border="1px solid #3b82f6"
                boxShadow="0 1px 4px rgba(0, 0, 0, 0.08)"
              >

                {/* Display KPI 1.1 specific data */}
                {String(param?.no) === '1.1' && (() => {
                  try {
                    const workText = app.work || '';

                    // Try multiple regex patterns to find the JSON
                    let jsonMatch = workText.match(/\{"totalLessons":\s*\d+\}/);
                    if (!jsonMatch) {
                      jsonMatch = workText.match(/\{[^}]*"totalLessons"[^}]*\}/);
                    }
                    if (!jsonMatch) {
                      // Try to find any JSON-like structure with totalLessons
                      jsonMatch = workText.match(/\{.*totalLessons.*\}/);
                    }

                    if (jsonMatch) {
                      const requirementData = JSON.parse(jsonMatch[0]);
                      return (
                        <Box mb={2} p={2} bg="white" borderRadius={4} border="1px solid #e2e8f0">
                          <Box display="flex" justifyContent="space-between" alignItems="center" p={2} bg="gray.50" borderRadius={4}>
                            <Text fontSize="sm" fontWeight={600}>Umumiy darslar soni:</Text>
                            <Text fontSize="md" fontWeight={700} color="blue.600">{requirementData.totalLessons || '0'}</Text>
                          </Box>
                        </Box>
                      );
                    }
                  } catch (e) {
                    console.error('Error parsing KPI 1.1 data:', e);
                  }
                  return null;
                })()}

                {/* Display files if available */}
                {app.files && app.files.length > 0 && (
                  <Box mt={2} pt={2} borderTop="1px solid #f3f4f6">
                    <Text fontWeight={600} mb={1} fontSize="xs" color="blue.800">Biriktirilgan hujjatlar</Text>
                    <VStack align="stretch" spacing={1}>
                      {app.files.map((file, idx) => {
                        const fileName = file.name || 'Hujjat';
                        const filePath = file.file || '';

                        return (
                          <Box
                            key={idx}
                            p={2}
                            bg="blue.50"
                            borderRadius={4}
                            border="1px solid #dbeafe"
                          >
                            <a
                              href={`${FILE_URL}${filePath}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: '#3b82f6',
                                textDecoration: 'none',
                                fontWeight: 600,
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}
                            >
                              📄 {fileName}
                            </a>
                          </Box>
                        );
                      })}
                    </VStack>
                  </Box>
                )}

                {app.confirmed_user?.first_name && (
                  <Box mt={2} pt={2} borderTop="1px solid #f3f4f6">
                    <Text fontSize="xs" color="blue.700" fontWeight={600} mb={1}>Tasdiqlovchi</Text>
                    <Text fontWeight={600} color="blue.900" fontSize="xs">
                      {formatFullName(app.confirmed_user.first_name, app.confirmed_user.last_name, app.confirmed_user.middle_name)}
                    </Text>
                  </Box>
                )}
              </Box>
            ))}
          </Card>
        )}

        {/* Mening arizalarim section */}
        {myApps.map(app => {
          // Check if this is a kafedra point card
          const isKafedraCard = isKafedraPoint(app);

          return (
            <Box
              key={app.id}
              p={3}
              mb={3}
              borderRadius={8}
              style={{ background: isKafedraCard ? 'transparent' : statusBg(app.status, app) }}
              border={isKafedraCard ? 'none' : "1px solid #e2e8f0"}
            >
              {/* Render kafedra point confirmation card if applicable */}
              {isKafedraCard ? renderKafedraPointCard(app) : (
                <>
                  <Box mb={2} display="flex" justifyContent="space-between" alignItems="flex-start" gap={3}>
                    <Box flex={1}>
                      <Text fontWeight={600} mb={1}></Text>
                      {renderWork(app)}
                    </Box>
                    {/* Show points in top right corner for approved and rejected applications */}
                    {(app.status === 4 || app.status === 5) && !(String(app?.parameter?.code) === '2.5' && app.ball === 0) && (
                      <Box
                        bg={app.status === 4 ? "green.100" : "red.100"}
                        px={3}
                        py={1}
                        borderRadius="md"
                        border="1px solid"
                        borderColor={app.status === 4 ? "green.300" : "red.300"}
                        flexShrink={0}
                      >
                        <Text fontSize="lg" fontWeight={700} color={app.status === 4 ? "green.700" : "red.700"}>
                          {app.ball || 0} ball
                        </Text>
                      </Box>
                    )}
                  </Box>


                  {app.files && app.files.length > 0 && (
                    <Box mb={2}>
                      <Text fontWeight={600} mb={1}>Fayllar:</Text>
                      {app.files.map((file, idx) => {
                        // Check if this is a guidance file for parameter 1.3 or 1.4
                        const isGuidanceFile = (String(app.parameter?.code) === '1.3' ||
                          String(app.parameter?.code) === '1.4') &&
                          file.name?.startsWith('Rahbarlik_hujjati_');
                        // Check if this is a permission file for parameter 1.6 or 1.7
                        const isPermissionFile = (String(app.parameter?.code) === '1.6' ||
                          String(app.parameter?.code) === '1.7') &&
                          file.name?.startsWith('Ruxsatnoma_fayli_');
                        // Check if this is a certificate file for parameter 1.8
                        const isCertFile = String(app.parameter?.code) === '1.8' &&
                          file.name?.startsWith('Sertifikat_fayli_');
                        // Check if this is a document file for parameter 1.9
                        const isDocFile = String(app.parameter?.code) === '1.9' &&
                          file.name?.startsWith('Hujjat_fayli_');

                        return (
                          <Box key={idx} mb={1}>
                            <a href={`${FILE_URL}${file.file}`} target="_blank" rel="noopener noreferrer"
                              style={{ color: PRIMARY, textDecoration: 'underline' }}>
                              {isGuidanceFile ?
                                `📋 Rahbarlik hujjati: ${file.name || 'Fayl'}` :
                                isPermissionFile ?
                                  `📄 Ruxsatnoma fayli: ${file.name || 'Fayl'}` :
                                  isCertFile ?
                                    `🏅 Sertifikat fayli: ${file.name || 'Fayl'}` :
                                    isDocFile ?
                                      `📄 Hujjat fayli: ${file.name || 'Fayl'}` :
                                      (file.name || 'Fayl')
                              }
                            </a>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                  {/* Hide department stripe for kafedra points */}
                  {!isKafedraPoint(app) && renderDeptStripe(app)}

                  {/* Show edit/delete buttons for:
                      1. Applications without department decision
                      2. Rejected applications within 3 days */}
                  {!isKafedraPoint(app) && (() => {
                    const canEdit = !hasAnyDepartmentDecision(app);
                    const code = app?.parameter?.code;

                    // Check if rejected (for both single and multi-department parameters)
                    // For multi-department parameters, only consider rejected if ALL departments have decided
                    // and at least one rejected
                    let isRejected = false;
                    if (code === '1.1') {
                      // All three departments must have decided (not 0)
                      const allDecided = app.tt_status !== 0 && app.ts_status !== 0 && app.ov_status !== 0;
                      const anyRejected = app.tt_status === 2 || app.ts_status === 2 || app.ov_status === 2;
                      isRejected = allDecided && anyRejected;
                    } else if (code === '1.3' || code === '1.4') {
                      // Both departments must have decided
                      const allDecided = app.mn_status !== 0 && app.ov_status !== 0;
                      const anyRejected = app.mn_status === 2 || app.ov_status === 2;
                      isRejected = allDecided && anyRejected;
                    } else if (code === '3.1') {
                      // Both departments must have decided
                      const allDecided = app.tt_status !== 0 && app.ts_status !== 0;
                      const anyRejected = app.tt_status === 2 || app.ts_status === 2;
                      isRejected = allDecided && anyRejected;
                    } else if (code === '3.2' || code === '3.3') {
                      // All three departments must have decided
                      const allDecided = app.ov_status !== 0 && app.tt_status !== 0 && app.ts_status !== 0;
                      const anyRejected = app.ov_status === 2 || app.tt_status === 2 || app.ts_status === 2;
                      isRejected = allDecided && anyRejected;
                    } else if (code === '3.4') {
                      // Both departments must have decided
                      const allDecided = app.tt_status !== 0 && app.ts_status !== 0;
                      const anyRejected = app.tt_status === 2 || app.ts_status === 2;
                      isRejected = allDecided && anyRejected;
                    } else {
                      // For single-department parameters
                      isRejected = app.status === 5;
                    }

                    // Debug logging
                    if (isRejected) {
                      console.log('Rejected app debug:', {
                        id: app.id,
                        code: code,
                        status: app.status,
                        isRejected: isRejected,
                        rejected_at: app.rejected_at,
                        canEdit: canEdit
                      });
                    }

                    // Check if within 3 days of rejection
                    let canResubmit = false;
                    if (isRejected) {
                      if (app.rejected_at) {
                        const rejectedDate = new Date(app.rejected_at);
                        const now = new Date();
                        const daysSinceRejection = Math.floor((now - rejectedDate) / (1000 * 60 * 60 * 24));
                        canResubmit = daysSinceRejection <= 3;
                        console.log('Resubmit check:', {
                          rejectedDate: rejectedDate,
                          daysSinceRejection: daysSinceRejection,
                          canResubmit: canResubmit
                        });
                      } else {
                        // For backward compatibility: if rejected but no rejected_at, allow resubmit
                        canResubmit = true;
                        console.log('Resubmit check: No rejected_at, allowing resubmit for backward compatibility');
                      }
                    }

                    if (canEdit || canResubmit) {
                      return (
                        <Box>
                          {canResubmit && (
                            <Text fontSize="xs" color="orange.600" mb={2} fontWeight="600">
                              ⚠️ Rad etilgan arizani 3 kun ichida qayta yuborishingiz mumkin
                            </Text>
                          )}
                          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mt={4}>
                            <Box display="flex" gap={2}>
                              <Button
                                size="sm"
                                color="white"
                                bg={PRIMARY}
                                _hover={{ bg: PRIMARY, opacity: 0.8 }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  editApp(app);
                                }}
                              >
                                Tahrirlash
                              </Button>
                              <Button
                                size="sm"
                                bg="red.500"
                                color="white"
                                _hover={{ bg: 'red.600' }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  deleteApp(app.id);
                                }}
                              >
                                O'chirish
                              </Button>
                            </Box>

                            {/* Badge for resent applications - same line as buttons */}
                            {/* Show badge if app was rejected before (has rejected_at) but is now back in pending status */}
                            {app.rejected_at && !isRejected && (
                              <Badge colorScheme="orange" fontSize="sm" px={3} py={1}>
                                🔄 Qayta yuborilgan ariza
                              </Badge>
                            )}
                          </Box>
                        </Box>
                      );
                    }
                    return null;
                  })()}
                </>
              )}
            </Box>
          );
        })}
      </Box>
    </Layout>
  );
};

export default KpiDetailTeacher;